import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Clock,
  ExternalLink,
  FileText,
  Flag,
  Hourglass,
  Info,
  Loader2,
  Pause,
  PauseCircle,
  PencilLine,
  Play,
  Sparkles,
} from 'lucide-react';
import { ModuleLayout } from '../../../components/layout/ModuleLayout';

import { EnterprisePage } from '../../../components/layout/EnterprisePage';
import type { StatusCapsuleItem } from '../../../components/layout/StatusCapsule';
import { PageHeader } from '../../../components/layout/PageHeader';
import { DataGridEnterprise } from '../../../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../../../components/layout/DataGridEnterprise';
import { EnterpriseBadge } from '../../../components/layout/EnterpriseTable';
import { useAppToast } from '../../../components/AppToast';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useOperationalContext } from '../../../contexts/OperationalContext';
import { useExecutionSelection } from '../../../contexts/ExecutionSelectionContext';
import { GainsMonitorService } from '../../../services/gainsMonitorService';
import { loadMyPlanActions, type AssignedPlanAction } from '../../../services/actionSyncService';
import type { KaizenRecord } from '../../../types/gainsMonitor';
import { useMyTasks } from '../hooks/useMyTasks';
import { useMyTaskDetail } from '../hooks/useMyTaskDetail';
import { useMyTaskMutations } from '../hooks/useMyTaskMutations';
import { useMinhaMesaStore } from '../store/minhaMesaStore';
import { MinhaMesaChecklist } from '../components/MinhaMesaChecklist';
import { MinhaMesaTimeline } from '../components/MinhaMesaTimeline';
import { TaskExecutionCockpit } from '../components/TaskExecutionCockpit';
import type { CockpitMetric, CockpitStatusVariant } from '../components/TaskExecutionCockpit';
import type { OperationalSummaryData } from '../components/TaskExecutionSummary';
import { buildPendencyItems, isFieldValueEmpty, primaryActionSubtitle, pendenciesSemaphoreTone, problemsSemaphoreTone } from '../utils/pendenciesUtils';
import { extractFormQuickSummary } from '../utils/formQuickSummaryUtils';
import { ExecutionTimelineSummary } from '../components/ExecutionTimelineSummary';
import { ExecutionsActionBar } from '../components/ExecutionsActionBar';
import { TaskExecutionContextPanel } from '../components/TaskExecutionContextPanel';
import { DemandDetailsDrawer } from '../components/DemandDetailsDrawer';
import { ExecutionGuideStepper } from '../components/ExecutionGuideStepper';
// MinhaMesaHandoff removed — observation card now inline
import { ProcessArtifactsPanel } from '../components/ProcessArtifactsPanel';
import { SmartInstructionsBanner, computeSmartInstructions } from '../components/SmartInstructions';
import { validateTaskCompletion, getActiveWarnings, getVisibleFields, validateBlocks } from '../../shared/services/executionEngine';
import { saveFieldResponse, saveChecklistResponse, saveAllFieldResponses, saveAllChecklistResponses, logExecutionEvent } from '../../shared/services/executionDataService';
import { useExecutionConfig } from '../hooks/useExecutionConfig';
import { useExecutionBlocks } from '../hooks/useExecutionBlocks';
import { processTaskCompletion } from '../../shared/services/executionMetricsService';
import { MinhaMesaInbox, getInboxCount } from '../components/MinhaMesaInbox';
import { InboxQueueRail } from '../components/InboxQueueRail';
import { RoadmapAgendaStrip } from '../components/RoadmapAgendaStrip';
import { ProjectRoadmapService } from '../../../services/projectRoadmapService';
import { InlineSourceDetailPanel } from '../components/InlineSourceDetailPanel';
import { useNotifications } from '../../../hooks/useNotifications';
import { listInstanceArtifacts } from '../services/artifactService';
import { TaskTransferModal } from '../components/TaskTransferModal';
import { RegisterProblemModal } from '../components/RegisterProblemModal';
import { ReturnStepModal } from '../components/ReturnStepModal';
import { MinhaMesaRibbon } from '../components/MinhaMesaRibbon';
import type { WorkViewMode } from '../components/MinhaMesaRibbon';
import { UnifiedSupportPanel } from '../components/UnifiedSupportPanel';
import { RequestCatalogPanel, RequestOpenStage } from '../../../components/launcher/InlineRequestLaunch';
import type { EntryFlow } from '../../../services/entryFlowService';
import { TaskExecutionWorkbench } from '../components/TaskExecutionWorkbench';
import { formatIdleDurationLabel, formatPriorityLabel, buildExecutionTimeline, buildRecentMovementEvents, formatRelativeTime } from '../utils/taskDisplayUtils';
import { useUnifiedPanelBadges } from '../hooks/useUnifiedPanelBadges';
import type { SupportPanelTab } from '../hooks/useUnifiedPanelBadges';
import { AlertsDrawer } from '../components/AlertsDrawer';
import { SlaDrawer } from '../components/SlaDrawer';
import { IndicatorsDrawer } from '../components/IndicatorsDrawer';
import { OperationalExecutionCenter } from '../components/OperationalExecutionCenter';
import { WorkKanbanView } from '../components/WorkKanbanView';
import type { CardDensity } from '../components/WorkKanbanView';
import { WorkTimelineView } from '../components/WorkTimelineView';
import { WorkItemPreviewDrawer } from '../components/WorkItemPreviewDrawer';
import { useExecutionUIStore } from '../store/executionUIStore';
import { fetchMyWorkItems } from '../services/unifiedWorkService';
import { actionVisibleOnDesk } from '../../../components/ops/exceptionLoop';
import type { UnifiedWorkItem } from '../types/workItem';
import { useWorkFilters } from '../hooks/useWorkFilters';
import type { SortMode } from '../hooks/useWorkFilters';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { uploadTaskAttachments } from '../services/taskAttachmentService';
import { resolveEffectiveState, getStateDisplay, type TaskState } from '../engine/taskStateMachine';
import type { TaskDetailItem, TaskListItem } from '../../shared/types/task';
import {
  formatDurationClockFromMinutes,
  formatSlaSmartLabel,
  getOperationalRiskLevel,
  getOperationalRiskPresentation,
  getOperationalRiskRank,
  getRemainingMinutesFromDeadline,
  getTaskTotalSlaMinutes,
} from '../../shared/utils/operationalRisk';

const PRIORITY_FILTERS = [
  { label: 'Todas prioridades', value: 'all' },
  { label: 'Critica', value: 'critica' },
  { label: 'Alta', value: 'alta' },
  { label: 'Media', value: 'media' },
  { label: 'Baixa', value: 'baixa' },
] as const;

const PRIORITY_ORDER: Record<string, number> = {
  critica: 4,
  alta: 3,
  media: 2,
  baixa: 1,
};

type LinkedActionRecord = Partial<KaizenRecord> & {
  id: string;
  workflow_id?: string | null;
  project_id?: string | null;
  target_date?: string | Date | null;
  planned_date?: string | Date | null;
  status?: string | null;
  type?: string | null;
  title?: string | null;
};

function formatExecutionCode(task: Pick<TaskListItem, 'referenceCode' | 'instanceId' | 'instanceTitle'>) {
  return task.referenceCode ?? task.instanceTitle ?? task.instanceId.slice(0, 8).toUpperCase();
}

function resolveTaskRequesterName(task: TaskDetailItem): string {
  const meta = task.metadata ?? {};
  const candidates = [
    meta.initiated_by_name,
    meta.requester_name,
    meta.created_by_name,
    meta.requester,
    meta.initiated_by,
  ];
  const resolved = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return typeof resolved === 'string' ? resolved.trim() : 'Não informado';
}

function formatTaskOpenedAt(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getTaskRiskLevel(task: Pick<TaskListItem, 'slaDeadline' | 'createdAt' | 'metadata'>) {
  const remainingMinutes = getRemainingMinutesFromDeadline(task.slaDeadline);
  const totalSlaMinutes = getTaskTotalSlaMinutes(task);
  return getOperationalRiskLevel(remainingMinutes, totalSlaMinutes);
}

function compareOperationalQueue(left: TaskListItem, right: TaskListItem) {
  const leftRiskRank = getOperationalRiskRank(getTaskRiskLevel(left));
  const rightRiskRank = getOperationalRiskRank(getTaskRiskLevel(right));

  if (leftRiskRank !== rightRiskRank) {
    return rightRiskRank - leftRiskRank;
  }

  const leftRemaining = getRemainingMinutesFromDeadline(left.slaDeadline);
  const rightRemaining = getRemainingMinutesFromDeadline(right.slaDeadline);

  if (leftRemaining === null && rightRemaining !== null) return 1;
  if (leftRemaining !== null && rightRemaining === null) return -1;
  if (leftRemaining !== null && rightRemaining !== null && leftRemaining !== rightRemaining) {
    return leftRemaining - rightRemaining;
  }

  const leftPriority = PRIORITY_ORDER[(left.priority ?? '').toLowerCase()] ?? 0;
  const rightPriority = PRIORITY_ORDER[(right.priority ?? '').toLowerCase()] ?? 0;
  if (leftPriority !== rightPriority) {
    return rightPriority - leftPriority;
  }

  return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
}

function formatPriority(priority?: string | null) {
  return priority ? priority.toUpperCase() : 'SEM_PRIORIDADE';
}

function formatCompactDate(value?: string | Date | null) {
  if (!value) return 'N/D';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/D';
  return date.toLocaleDateString('pt-BR');
}

function getActionRisk(targetDate?: string | Date | null) {
  if (!targetDate) {
    return { label: 'OK', className: 'text-slate-500 dark:text-slate-400' };
  }

  const dueAt = targetDate instanceof Date ? targetDate : new Date(targetDate);
  if (Number.isNaN(dueAt.getTime())) {
    return { label: 'OK', className: 'text-slate-500 dark:text-slate-400' };
  }

  const remainingHours = (dueAt.getTime() - Date.now()) / (1000 * 60 * 60);
  if (remainingHours < 0) {
    return { label: 'CRITICO', className: 'text-rose-700 dark:text-rose-300' };
  }
  if (remainingHours <= 72) {
    return { label: 'ALERTA', className: 'text-amber-700 dark:text-amber-300' };
  }
  return { label: 'OK', className: 'text-emerald-700 dark:text-emerald-300' };
}

function formatActionType(value?: string | null) {
  switch (value) {
    case 'reducao_lead_time':
      return 'Reducao LT';
    case 'eliminacao_desperdicio':
      return 'Desperdicio';
    case 'automacao':
      return 'Automacao';
    case 'padronizacao':
      return 'Padronizacao';
    case 'qualidade':
      return 'Qualidade';
    case 'ergonomia':
      return 'Ergonomia';
    default:
      return 'Acao';
  }
}

function formatActionStatus(value?: string | null) {
  switch (value) {
    case 'planejado':
      return 'Planejado';
    case 'em_implementacao':
      return 'Em execucao';
    case 'implementado':
      return 'Implementado';
    case 'em_monitoramento':
      return 'Monitoramento';
    case 'regredido':
      return 'Regredido';
    case 'sustentado':
      return 'Sustentado';
    default:
      return 'Aberto';
  }
}

function formatActionOrigin(action: LinkedActionRecord) {
  if (action.workflow_id) {
    return `EXEC ${action.workflow_id.slice(0, 8).toUpperCase()}`;
  }
  if (action.project_id) {
    return `PROJ ${action.project_id.slice(0, 8).toUpperCase()}`;
  }
  return 'SEM_VINCULO';
}

function getLinkedActionDeviation(action: LinkedActionRecord) {
  const baselineLead = toFiniteNumber(action.baseline_lead_time);
  const targetLead = toFiniteNumber(action.target_lead_time);
  const currentLead = toFiniteNumber(action.current_lead_time);

  if (baselineLead !== null && targetLead !== null && currentLead !== null && targetLead > 0) {
    return getDeviationPresentation(((currentLead - targetLead) / targetLead) * 100);
  }

  const baselineCycle = toFiniteNumber(action.baseline_cycle_time);
  const targetCycle = toFiniteNumber(action.target_cycle_time);
  const currentCycle = toFiniteNumber(action.current_cycle_time);

  if (baselineCycle !== null && targetCycle !== null && currentCycle !== null && targetCycle > 0) {
    return getDeviationPresentation(((currentCycle - targetCycle) / targetCycle) * 100);
  }

  return { label: 'N/D', className: 'text-slate-500 dark:text-slate-400' };
}

type OperationalTaskLike = Pick<
  TaskListItem,
  'claimedAt' | 'completedAt' | 'createdAt' | 'metadata' | 'slaDeadline' | 'startedAt' | 'status' | 'updatedAt'
>;

type TaskOperationalSnapshot = {
  enteredAt: string | null;
  claimedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  tpStdMinutes: number | null;
  traStdMinutes: number | null;
  slaLimitMinutes: number | null;
  tpRealMinutes: number | null;
  traRealMinutes: number | null;
  queueMinutes: number | null;
  waitingBeforeClaimMinutes: number | null;
  deviationPercent: number | null;
  slaDeviationPercent: number | null;
  hasBaseline: boolean;
};

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function diffMinutes(start?: string | null, end?: string | null) {
  if (!start || !end) return null;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(diff) || diff < 0) return null;
  return diff / 60000;
}

function resolveEnteredAt(task: OperationalTaskLike) {
  return (
    (typeof task.metadata?.operationalMetrics === 'object' && task.metadata?.operationalMetrics && 'enteredAt' in task.metadata.operationalMetrics
      ? (task.metadata.operationalMetrics as Record<string, unknown>).enteredAt as string | null | undefined
      : undefined) ??
    (typeof task.metadata?.entered_at === 'string' ? task.metadata.entered_at : null) ??
    task.startedAt ??
    task.claimedAt ??
    task.updatedAt ??
    task.createdAt ??
    null
  );
}

function resolveStandardMinutes(task: OperationalTaskLike, kind: 'tp' | 'tra') {
  if (kind === 'tp') {
    return (
      toFiniteNumber(task.metadata?.tp_standard) ??
      toFiniteNumber(task.metadata?.tpStandard) ??
      toFiniteNumber(task.metadata?.lt) ??
      (toFiniteNumber(task.metadata?.slaHours) !== null ? Number(task.metadata?.slaHours) * 60 : null)
    );
  }

  return (
    toFiniteNumber(task.metadata?.tra_standard) ??
    toFiniteNumber(task.metadata?.traStandard) ??
    toFiniteNumber(task.metadata?.tt)
  );
}

function resolveSlaLimitMinutes(task: OperationalTaskLike) {
  return (
    getTaskTotalSlaMinutes(task) ??
    (toFiniteNumber(task.metadata?.slaHours) !== null ? Number(task.metadata?.slaHours) * 60 : null)
  );
}

/** Calculate TRA from event logs: sum of active periods (started/resumed → paused/completed) */
function calculateTraFromEvents(events: Array<{ action?: string; justification?: string; metadata?: any; created_at?: string }>, nowIso: string): number | null {
  if (!events || events.length === 0) return null;

  let totalMs = 0;
  let activeStart: number | null = null;

  const sorted = [...events].sort((a, b) =>
    new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime()
  );

  for (const ev of sorted) {
    const action = ev.action?.toLowerCase() ?? '';
    const justification = ev.justification?.toLowerCase() ?? '';
    const metaTo = (ev.metadata?.to ?? '').toLowerCase();
    const ts = new Date(ev.created_at ?? 0).getTime();
    if (!Number.isFinite(ts)) continue;

    const isStart = action.includes('started') || justification.includes('iniciada') || metaTo === 'in_progress';
    const isResume = action.includes('resumed') || justification.includes('retomada');
    const isPause = action.includes('paused') || justification.includes('pausada') || metaTo === 'paused';
    const isComplete = action.includes('completed') || justification.includes('concluida') || metaTo === 'completed';

    if (isStart || isResume) {
      if (activeStart === null) activeStart = ts;
    } else if (isPause || isComplete) {
      if (activeStart !== null) {
        totalMs += ts - activeStart;
        activeStart = null;
      }
    }
  }

  // If still active (started but not yet paused/completed), count until now
  if (activeStart !== null) {
    totalMs += new Date(nowIso).getTime() - activeStart;
  }

  return totalMs > 0 ? totalMs / 60000 : null;
}

function buildOperationalSnapshot(task: OperationalTaskLike | null | undefined, nowMs: number, taskLogs?: Array<{ action?: string; justification?: string; metadata?: any; created_at?: string }>): TaskOperationalSnapshot | null {
  if (!task) return null;

  const nowIso = new Date(nowMs).toISOString();
  const enteredAt = resolveEnteredAt(task);
  const claimedAt = task.claimedAt ?? null;
  const startedAt = task.startedAt ?? null;
  const completedAt = task.completedAt ?? null;
  const tpStdMinutes = resolveStandardMinutes(task, 'tp');
  const traStdMinutes = resolveStandardMinutes(task, 'tra');
  const slaLimitMinutes = resolveSlaLimitMinutes(task);
  const tpRealMinutes = diffMinutes(enteredAt, completedAt ?? nowIso);
  // TRA: prefer event-based calculation (sum of active periods), fallback to startedAt → now
  const traFromEvents = calculateTraFromEvents(taskLogs ?? [], nowIso);
  const traRealMinutes = traFromEvents ?? (startedAt ? diffMinutes(startedAt, completedAt ?? nowIso) : null);
  const queueMinutes = tpRealMinutes !== null
    ? Math.max(0, tpRealMinutes - (traRealMinutes ?? 0))
    : null;
  const waitingBeforeClaimMinutes = enteredAt
    ? diffMinutes(enteredAt, claimedAt ?? nowIso)
    : null;
  const deviationPercent = tpStdMinutes && tpRealMinutes !== null
    ? ((tpRealMinutes - tpStdMinutes) / tpStdMinutes) * 100
    : traStdMinutes && traRealMinutes !== null
      ? ((traRealMinutes - traStdMinutes) / traStdMinutes) * 100
      : null;
  const slaDeviationPercent = slaLimitMinutes && tpRealMinutes !== null
    ? ((tpRealMinutes - slaLimitMinutes) / slaLimitMinutes) * 100
    : null;

  return {
    enteredAt,
    claimedAt,
    startedAt,
    completedAt,
    tpStdMinutes,
    traStdMinutes,
    slaLimitMinutes,
    tpRealMinutes,
    traRealMinutes,
    queueMinutes,
    waitingBeforeClaimMinutes,
    deviationPercent,
    slaDeviationPercent,
    hasBaseline: tpStdMinutes !== null || traStdMinutes !== null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORE-BASED QUEUE PRIORITIZATION
// score = (atrasoSLA * 3) + (prioridade * 2) - (tempoFila * 0.5)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a numeric score for a task in the queue.
 * Higher score = should be processed first.
 */
export function calcQueueScore(task: TaskListItem, nowMs: number): number {
  const now = nowMs;

  // atrasoSLA: minutes past deadline (positive = overdue, negative = time remaining)
  const atrasoSLA = task.slaDeadline
    ? (now - new Date(task.slaDeadline).getTime()) / 60000
    : 0;

  // prioridade: numeric weight from task priority field
  const prioValue = PRIORITY_ORDER[(task.priority ?? '').toLowerCase()] ?? 0;

  // tempoFila: minutes since available_at (or createdAt as fallback)
  const availableRef = task.availableAt ?? task.createdAt;
  const tempoFila = availableRef
    ? Math.max(0, (now - new Date(availableRef).getTime()) / 60000)
    : 0;

  return (atrasoSLA * 3) + (prioValue * 2) - (tempoFila * 0.5);
}

/** Returns true when the task score is critically high (needs immediate attention) */
export function isQueueScoreCritical(task: TaskListItem, nowMs: number): boolean {
  if (task.isOverdue) return true;
  const score = calcQueueScore(task, nowMs);
  // Critical if: SLA overdue OR score > 10 (e.g. max priority + >10min overdue)
  return score > 10;
}

function compareQueueByOperationalPriority(left: TaskListItem, right: TaskListItem, nowMs: number) {
  const leftRiskRank = getOperationalRiskRank(getTaskRiskLevel(left));
  const rightRiskRank = getOperationalRiskRank(getTaskRiskLevel(right));

  if (leftRiskRank !== rightRiskRank) {
    return rightRiskRank - leftRiskRank;
  }

  const leftTp = buildOperationalSnapshot(left, nowMs)?.tpRealMinutes ?? 0;
  const rightTp = buildOperationalSnapshot(right, nowMs)?.tpRealMinutes ?? 0;
  if (leftTp !== rightTp) {
    return rightTp - leftTp;
  }

  return compareOperationalQueue(left, right);
}

function getDeviationPresentation(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { label: 'N/D', className: 'text-slate-500 dark:text-slate-400' };
  }
  if (value > 0) {
    return { label: `+${value.toFixed(1)}%`, className: 'text-rose-700 dark:text-rose-300' };
  }
  if (value < 0) {
    return { label: `${value.toFixed(1)}%`, className: 'text-emerald-700 dark:text-emerald-300' };
  }
  return { label: '0.0%', className: 'text-slate-700 dark:text-slate-300' };
}

// ─────────────────────────────────────────────────────────────────────────────
// ETA — PREVISÃO DE CONCLUSÃO
// ETA(posição) = now + posição * avgDurationMin
// ─────────────────────────────────────────────────────────────────────────────

const ETA_DEFAULT_MIN = 30;

/** Returns average actual_duration in minutes from a list of tasks. Falls back to ETA_DEFAULT_MIN. */
export function calcMediaDuracao(tasks: TaskListItem[]): number {
  const durations = tasks
    .map((t) => t.actualDuration)
    .filter((d): d is number => typeof d === 'number' && d > 0);
  if (durations.length === 0) return ETA_DEFAULT_MIN;
  return durations.reduce((sum, d) => sum + d, 0) / durations.length;
}

/**
 * Returns a human-readable ETA string for a task at `posicao` (0-based) in the queue.
 * - < 60 min → "em X min"
 * - ≥ 60 min → time of day "HH:MM"
 */
export function calcularETA(posicao: number, avgDurationMin: number, nowMs: number): string {
  const etaMs = nowMs + posicao * avgDurationMin * 60_000;
  const diffMin = Math.round((etaMs - nowMs) / 60_000);
  if (diffMin <= 0) return 'agora';
  if (diffMin < 60) return `em ${diffMin} min`;
  const date = new Date(etaMs);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// ─────────────────────────────────────────────────────────────────────────────
// WIP LIMIT — Limite de trabalho em processo por etapa
// ─────────────────────────────────────────────────────────────────────────────

/** Default WIP limit applied to all stages without a specific override. */
const WIP_LIMIT_DEFAULT = 5;

/**
 * Per-stage WIP overrides (keyed by stageId or stageName).
 * Add entries here to tune per-stage limits without a DB config screen.
 */
const WIP_LIMIT_PER_STAGE: Record<string, number> = {
  // Example: 'Aprovação': 2,
};

interface WipStatus {
  current: number;
  limit: number;
  exceeded: boolean;
}

/**
 * Returns the current WIP status for a given stage.
 * Counts tasks with status `in_progress` in `allTasks` that share the same stageId or stageName.
 */
export function checkWIP(
  stageId: string,
  stageName: string,
  allTasks: TaskListItem[],
): WipStatus {
  const limit =
    WIP_LIMIT_PER_STAGE[stageId] ??
    WIP_LIMIT_PER_STAGE[stageName] ??
    WIP_LIMIT_DEFAULT;
  const current = allTasks.filter(
    (t) => t.status === 'in_progress' && (t.stageId === stageId || t.stageName === stageName),
  ).length;
  return { current, limit, exceeded: current >= limit };
}

/* ── Status badge helpers for TaskListItem DataGrid ── */

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'slate' },
  waiting: { label: 'Aguardando', color: 'blue' },
  in_progress: { label: 'Em Execução', color: 'emerald' },
  completed: { label: 'Concluída', color: 'slate' },
  failed: { label: 'Devolvida', color: 'red' },
  paused: { label: 'Pausada', color: 'amber' },
};

const PRIORITY_BADGE: Record<string, { label: string; color: string }> = {
  critica: { label: 'Crítica', color: 'red' },
  alta: { label: 'Alta', color: 'amber' },
  media: { label: 'Média', color: 'slate' },
  baixa: { label: 'Baixa', color: 'sky' },
};

interface EtaContext {
  avgDurationMin: number;
  positionMap: Map<string, number>;
  nowMs: number;
}

function taskListColumns(
  onClaimTask?: (task: TaskListItem) => void,
  queueSortMode: 'risk' | 'tp' | 'score' = 'score',
  etaContext?: EtaContext,
): DataGridColumn<TaskListItem>[] {
  return [
    {
      key: 'code', label: 'Código', width: '118px', sortable: true, mono: true,
      render: (r) => {
        const remaining = getRemainingMinutesFromDeadline(r.slaDeadline);
        const total = getTaskTotalSlaMinutes(r);
        const pres = getOperationalRiskPresentation(getOperationalRiskLevel(remaining, total));

        return (
          <div className="flex items-center gap-1.5">
            <span aria-hidden="true">{pres.icon}</span>
            <span>{r.referenceCode ?? r.instanceId.slice(0, 8).toUpperCase()}</span>
          </div>
        );
      },
    },
    {
      key: 'stage', label: 'Etapa', sortable: true,
      render: (r) => r.stageName,
    },
    {
      key: 'instance', label: 'Execução',
      render: (r) => r.instanceTitle ?? '—',
    },
    {
      key: 'status', label: 'Status', width: '110px', sortable: true,
      render: (r) => {
        const b = STATUS_BADGE[r.status ?? ''] ?? { label: r.status ?? '—', color: 'slate' };
        return <EnterpriseBadge label={b.label} color={b.color} />;
      },
    },
    {
      key: 'priority', label: 'Prioridade', width: '130px', sortable: true, mono: true,
      render: (r) => {
        const key = (r.priority ?? '').toLowerCase();
        const b = PRIORITY_BADGE[key];
        const isCritical = isQueueScoreCritical(r, Date.now());
        const isScoreMode = queueSortMode === 'score';
        return (
          <span className="inline-flex items-center gap-1">
            {b ? (
              <span className={`font-semibold ${
                key === 'critica' || isCritical ? 'text-rose-600 dark:text-rose-400'
                : key === 'alta' ? 'text-amber-600 dark:text-amber-400'
                : ''
              }`}>{b.label}</span>
            ) : '—'}
            {isScoreMode && isCritical && (
              <span className="rounded bg-rose-500/15 px-1 py-0.5 text-[8px] font-bold uppercase tracking-[0.06em] text-rose-600 dark:text-rose-400">
                Auto
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'sla', label: queueSortMode === 'risk' ? '▲ SLA / Tempo' : 'SLA / Tempo', width: '132px', mono: true,
      render: (r) => {
        const remaining = getRemainingMinutesFromDeadline(r.slaDeadline);
        const total = getTaskTotalSlaMinutes(r);
        const risk = getOperationalRiskLevel(remaining, total);
        const pres = getOperationalRiskPresentation(risk);

        return (
          <div className="flex flex-col leading-tight">
            <span className={`font-bold tabular-nums ${pres.className}`}>{formatDurationClockFromMinutes(remaining)}</span>
            <span className={`font-mono text-[9px] uppercase ${pres.className}`}>{pres.icon} {pres.label}</span>
          </div>
        );
      },
    },
    {
      key: 'risk', label: 'Risco', width: '96px', mono: true,
      render: (r) => {
        const remaining = getRemainingMinutesFromDeadline(r.slaDeadline);
        const total = getTaskTotalSlaMinutes(r);
        const risk = getOperationalRiskLevel(remaining, total);
        const pres = getOperationalRiskPresentation(risk);
        const badgeTone = pres.label === 'CRITICO'
          ? 'bg-rose-500/10 text-rose-400'
          : pres.label === 'ALERTA'
            ? 'bg-amber-500/10 text-amber-400'
            : 'bg-emerald-500/10 text-emerald-400';

        return (
          <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${badgeTone}`}>
            {pres.label === 'CRITICO' && <AlertTriangle size={10} />}
            {pres.icon} {pres.label}
          </span>
        );
      },
    },
    {
      key: 'assignee', label: 'Executor', width: '140px',
      render: (r) => r.assignedUserName ?? r.assignedToName ?? r.assignedTo ?? '—',
    },
    {
      key: 'date', label: queueSortMode === 'tp' ? '▲ Criada em' : 'Criada em', width: '110px', mono: true, sortable: true,
      render: (r) => {
        if (!r.createdAt) return '—';
        return new Date(r.createdAt).toLocaleDateString('pt-BR');
      },
    },
    {
      key: 'eta', label: 'Previsão', width: '90px', mono: true,
      render: (r) => {
        if (!etaContext) return '—';
        const pos = etaContext.positionMap.get(r.id);
        if (pos === undefined) return '—';
        const label = calcularETA(pos, etaContext.avgDurationMin, etaContext.nowMs);
        const isUrgent = pos === 0;
        return (
          <span className={`tabular-nums ${
            isUrgent ? 'font-bold text-emerald-600 dark:text-emerald-400' : 'text-[var(--text-secondary)]'
          }`}>
            {label}
          </span>
        );
      },
    },
  ];
}

export const MinhaMesaPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const departmentFilterId = searchParams.get('dept_id') || null;
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { showToast, ToastContainer } = useAppToast();
  const userId = user?.id;
  const { notifications, markRead: markNotifRead, markAllRead: markAllNotifsRead } = useNotifications(userId);
  const inboxCount = useMemo(() => getInboxCount(notifications), [notifications]);
  const userOrganizationId = user?.organization_id ?? user?.organizationId;
  const organizationId = workspace?.organizationId ?? userOrganizationId;

  const {
    selectedTaskId,
    formState,
    checklistState,
    notes,
    selectTask,
    setFormField,
    setFormState,
    toggleChecklistItem,
    setChecklistState,
    setNotes,
    resetTaskState,
  } = useMinhaMesaStore();

  const {
    setActiveExecution,
    setActiveStage,
    setOperationalMetrics,
  } = useOperationalContext();

  // Read global selection context (e.g. navigating from OpsCenter with a task pre-selected)
  const { selectedTaskId: globalSelectedTaskId, selectTask: globalSelectTask, selectInstance: globalSelectInstance } = useExecutionSelection();

  const [priorityFilter, setPriorityFilter] = useState<(typeof PRIORITY_FILTERS)[number]['value']>('all');
  const [executionFilter, setExecutionFilter] = useState('');
  const [queueSortMode, setQueueSortMode] = useState<'risk' | 'tp' | 'score'>('score');
  const [liveNow, setLiveNow] = useState(() => Date.now());
  const [queueNow, setQueueNow] = useState(() => Date.now());
  const [workViewMode, setWorkViewModeRaw] = useState<WorkViewMode>(() => {
    try {
      const stored = localStorage.getItem('workflow.minha-mesa.viewMode');
      if (stored === 'list' || stored === 'kanban' || stored === 'timeline') return stored;
    } catch { /* noop */ }
    return 'list';
  });
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('Aguardando Informação');
  // Inbox split-screen rail toggle (Prompt 30)

  // Card density (1–4 cards per row in kanban), persisted
  const [cardDensity, setCardDensityRaw] = useState<CardDensity>(() => {
    try { const v = localStorage.getItem('workflow.ui.density'); return v ? (Number(v) as CardDensity) : 2; } catch { return 2; }
  });
  const cycleDensity = useCallback(() => {
    setCardDensityRaw((prev) => {
      const next = (prev >= 5 ? 1 : prev + 1) as CardDensity;
      try { localStorage.setItem('workflow.ui.density', String(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  // Focus mode: prioritize execution over navigation
  const [focusMode, setFocusMode] = useState(false);
  const [viewportMode, setViewportMode] = useState<'full' | 'compact' | 'drawer'>('full');
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
  const toggleFocusMode = useCallback(() => setFocusMode((v) => !v), []);
  const toggleRightPanel = useCallback(() => setIsRightPanelOpen((v) => !v), []);

  useEffect(() => {
    const updateViewportMode = () => {
      const width = window.innerWidth;

      if (width < 1100) {
        setViewportMode('drawer');
        setIsRightPanelOpen(false);
        return;
      }

      if (width < 1400) {
        setViewportMode('compact');
        setIsRightPanelOpen(false);
        return;
      }

      setViewportMode('full');
      setIsRightPanelOpen(true);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);
    return () => window.removeEventListener('resize', updateViewportMode);
  }, []);

  // When switching views, deselect task and close overview preview
  const handleSetWorkViewMode = useCallback((mode: WorkViewMode) => {
    setPreviewWorkItem(null);
    if (selectedTaskId) {
      selectTask(null as any);
      resetTaskState();
      setActiveExecution(null, null);
      setActiveStage(null, null);
    }
    setWorkViewModeRaw(mode);
    try { localStorage.setItem('workflow.minha-mesa.viewMode', mode); } catch { /* noop */ }
  }, [selectedTaskId, selectTask, resetTaskState, setActiveExecution, setActiveStage]);

  useEffect(() => {
    if (!globalSelectedTaskId) return;
    setWorkViewModeRaw('list');
    setFocusMode(false);
    try { localStorage.setItem('workflow.minha-mesa.viewMode', 'list'); } catch { /* noop */ }
    selectTask(globalSelectedTaskId);
    globalSelectTask(null);
  }, [globalSelectedTaskId, selectTask, globalSelectTask]);

  const { activeDrawer, showRiskOnly, toggleDrawer, toggleRiskOnly, closeDrawer } = useExecutionUIStore();
  const [queueCollapsed, setQueueCollapsed] = useState(() => !departmentFilterId);
  const centerScrollRef = useRef<HTMLDivElement>(null);
  const [showBottomAction, setShowBottomAction] = useState(false);
  const [historyAccordionOpen, setHistoryAccordionOpen] = useState(false);
  const [supportPanelOpen, setSupportPanelOpen] = useState(true);
  const [supportTab, setSupportTab] = useState<SupportPanelTab>('demandas');
  const [requestCatalogOpen, setRequestCatalogOpen] = useState(false);
  const [launchFlow, setLaunchFlow] = useState<EntryFlow | null>(null);
  const [demandDetailsOpen, setDemandDetailsOpen] = useState(false);
  const [executionRail, setExecutionRail] = useState<'activity' | 'reference' | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const completionBarRef = useRef<HTMLDivElement | null>(null);
  const validationSectionRef = useRef<HTMLElement | null>(null);

  // Auto-pull state
  const [autoPullPhase, setAutoPullPhase] = useState<'idle' | 'counting' | 'pulling' | 'error'>('idle');
  const [autoPullCountdown, setAutoPullCountdown] = useState(3);
  const autoPullTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoPullTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoPullDebounceRef = useRef<number>(0);
  const autoPullContainerRef = useRef<HTMLDivElement | null>(null);
  const AUTO_PULL_DELAY = 3000;
  const AUTO_PULL_DEBOUNCE = 2000;

  const {
    tasks,
    claimedTasks,
    availableRoleTasks,
    isLoading,
    kpis,
    refetch,
  } = useMyTasks({
    userId,
    organizationId,
    departmentId: departmentFilterId,
  });

  const activeTask = useMemo(() => {
    if (claimedTasks.length === 0) return null;
    return [...claimedTasks].sort((left, right) => {
      const leftStatusRank = left.status === 'in_progress' ? 1 : 0;
      const rightStatusRank = right.status === 'in_progress' ? 1 : 0;
      if (leftStatusRank !== rightStatusRank) {
        return rightStatusRank - leftStatusRank;
      }
      return compareOperationalQueue(left, right);
    })[0] ?? null;
  }, [claimedTasks]);

  const { task: selectedTask, isLoading: detailLoading, logs, logsLoading, previousStage } = useMyTaskDetail(selectedTaskId);

  // ── Execution Config from v2 tables (with metadata fallback) ──
  const {
    config: execConfig,
    savedFormData,
    savedChecklistData,
    savedAttachments,
    isLoading: execConfigLoading,
    fromV2Tables,
  } = useExecutionConfig(selectedTask, organizationId ?? null);

  // ── Block-based execution model (v1) ──
  const {
    blocks: executionBlocks,
    responses: blockResponses,
    isLoading: blocksLoading,
    fromBlocks: hasBlocks,
  } = useExecutionBlocks(
    selectedTask?.stageId ?? null,
    organizationId ?? null,
    selectedTask?.id ?? null,
    selectedTask as any, // metadata fallback for executionConfig.blocks
  );

  // Artifacts for the current instance
  const artifactsQuery = useQuery({
    queryKey: ['instance-artifacts', selectedTask?.instanceId],
    enabled: Boolean(selectedTask?.instanceId),
    queryFn: () => listInstanceArtifacts(selectedTask!.instanceId),
    staleTime: 30_000,
    retry: false,
  });
  const instanceArtifacts = artifactsQuery.data ?? [];

  const mutations = useMyTaskMutations({
    organizationId,
    userId,
    userName: user?.name,
    onSuccess: async () => {
      await refetch();
      resetTaskState();
      // Auto-flow: select next best task after completion
      const nextBest = claimedTasks.find(t => t.id !== selectedTaskId) ?? queueTasks[0] ?? null;
      if (nextBest) {
        handleSelectTask(nextBest.id);
      }
    },
  });

  const linkedActionsQuery = useQuery<LinkedActionRecord[]>({
    queryKey: ['minha-mesa', organizationId, userId, 'linked-actions'],
    enabled: Boolean(userId && organizationId),
    staleTime: 60_000,
    queryFn: async () => {
      const responsibleCandidates = [user?.name, user?.email, userId].filter((value): value is string => Boolean(value));
      const actions = await GainsMonitorService.getKaizens({
        organization_id: organizationId,
        responsible: responsibleCandidates,
      });
      return (actions as LinkedActionRecord[]) ?? [];
    },
  });

  const planActionsQuery = useQuery<AssignedPlanAction[]>({
    queryKey: ['minha-mesa', organizationId, userId, 'plan-actions'],
    enabled: Boolean(userId && organizationId),
    staleTime: 30_000,
    queryFn: () => loadMyPlanActions(userId!, organizationId!),
  });
  const planActions = planActionsQuery.data ?? [];

  // Unified work items (workflow + actions + problems + monitoring).
  // Powers the Inbox Rail (left) in list mode AND the Kanban/Timeline views.
  const unifiedWorkQuery = useQuery<UnifiedWorkItem[]>({
    queryKey: ['minha-mesa', organizationId, userId, 'unified-work'],
    enabled: Boolean(userId && organizationId),
    staleTime: 30_000,
    queryFn: () => fetchMyWorkItems(organizationId!, userId!),
  });
  const roadmapAgendaQuery = useQuery({
    queryKey: ['minha-mesa', organizationId, userId, 'roadmap-agenda'],
    enabled: Boolean(userId && organizationId),
    queryFn: () => ProjectRoadmapService.listForMember(organizationId!, userId!, user?.email),
  });
  const unifiedWorkItems = unifiedWorkQuery.data ?? [];

  // Filtered + sorted unified items
  const { filters: workFilters, setFilters: setWorkFilters, resetFilters: resetWorkFilters, hasActiveFilters: hasActiveWorkFilters, filteredItems: filteredWorkItemsRaw } = useWorkFilters(unifiedWorkItems);

  // A ordenação da carteira vive em workFilters.sort. O atalho interno
  // (Auto / Risco / TP) só grava a ordenação quando o usuário escolhe.
  const queueSortTouchedRef = useRef(false);

  useEffect(() => {
    if (queueSortTouchedRef.current) {
      queueSortTouchedRef.current = false;
      return;
    }
    const mapped: 'score' | 'risk' | 'tp' =
      workFilters.sort === 'sla_asc' || workFilters.sort === 'priority' ? 'risk'
      : (workFilters.sort === 'lead_desc' || workFilters.sort === 'idle_desc') ? 'tp'
      : 'score';
    if (mapped !== queueSortMode) setQueueSortMode(mapped);
  }, [workFilters.sort, queueSortMode]);

  const handleQueueSortModeChange = useCallback((mode: 'risk' | 'tp' | 'score') => {
    queueSortTouchedRef.current = true;
    setQueueSortMode(mode);
    const mapped: SortMode =
      mode === 'risk' ? 'sla_asc'
      : mode === 'tp' ? 'idle_desc'
      : 'fila';
    setWorkFilters((prev) => (prev.sort === mapped ? prev : { ...prev, sort: mapped }));
  }, [setWorkFilters]);

  // Focus mode filter: only critical/overdue
  const filteredWorkItems = useMemo(() => {
    if (!focusMode) return filteredWorkItemsRaw;
    return filteredWorkItemsRaw.filter((item) => {
      if (item.is_overdue) return true;
      const deadline = item.source_context?.sla_deadline ?? item.due_date;
      if (deadline) {
        const remaining = (new Date(deadline).getTime() - Date.now()) / 60000;
        if (remaining < 120) return true; // less than 2h
      }
      if (item.priority === 'high' || item.priority === 'critical') return true;
      return false;
    });
  }, [filteredWorkItemsRaw, focusMode]);

  const queueTasks = useMemo(() => {
    return availableRoleTasks
      .filter((item) => {
        if (priorityFilter !== 'all' && (item.priority ?? '').toLowerCase() !== priorityFilter) {
          return false;
        }
        if (executionFilter.trim()) {
          const executionLabel = formatExecutionCode(item).toLowerCase();
          const executionTitle = (item.instanceTitle ?? '').toLowerCase();
          const filterValue = executionFilter.trim().toLowerCase();
          if (!executionLabel.includes(filterValue) && !executionTitle.includes(filterValue)) {
            return false;
          }
        }
        if (!showRiskOnly) return true;
        const risk = getTaskRiskLevel(item);
        return risk === 'CRITICO' || risk === 'ALERTA';
      })
      .sort((left, right) => {
        if (queueSortMode === 'score') {
          const diff = calcQueueScore(right, queueNow) - calcQueueScore(left, queueNow);
          if (diff !== 0) return diff;
          return compareOperationalQueue(left, right);
        }
        if (queueSortMode === 'tp') {
          const leftTp = buildOperationalSnapshot(left, queueNow)?.tpRealMinutes ?? 0;
          const rightTp = buildOperationalSnapshot(right, queueNow)?.tpRealMinutes ?? 0;
          if (leftTp !== rightTp) {
            return rightTp - leftTp;
          }
          return compareOperationalQueue(left, right);
        }

        return compareQueueByOperationalPriority(left, right, queueNow);
      });
  }, [availableRoleTasks, executionFilter, priorityFilter, queueNow, queueSortMode, showRiskOnly]);

  const linkedActions = useMemo(() => {
    return (linkedActionsQuery.data ?? [])
      .filter((action) => action.status !== 'sustentado')
      .sort((left, right) => {
        const leftTime = left.target_date ? new Date(left.target_date).getTime() : Number.POSITIVE_INFINITY;
        const rightTime = right.target_date ? new Date(right.target_date).getTime() : Number.POSITIVE_INFINITY;
        return leftTime - rightTime;
      });
  }, [linkedActionsQuery.data]);

  const nextRoleTask = queueTasks[0] ?? null;

  const riskTaskCount = useMemo(
    () => queueTasks.filter((t) => {
      const r = getTaskRiskLevel(t);
      return r === 'CRITICO' || r === 'ALERTA';
    }).length,
    [queueTasks],
  );

  const mesaStatusItems = useMemo<StatusCapsuleItem[]>(() => [
    ...(inboxCount > 0 ? [{ label: 'Inbox', value: inboxCount, color: 'blue' as const }] : []),
    { label: 'Ativas', value: claimedTasks.length, color: 'emerald' },
    { label: 'Na Fila', value: queueTasks.length, color: 'blue' },
    { label: 'Em Risco', value: riskTaskCount, color: riskTaskCount > 0 ? 'red' : 'slate' },
  ], [inboxCount, claimedTasks.length, queueTasks.length, riskTaskCount]);

  const prioritizedDeskTasks = useMemo(() => {
    const sortFn = (left: TaskListItem, right: TaskListItem): number => {
      if (queueSortMode === 'score') {
        const diff = calcQueueScore(right, queueNow) - calcQueueScore(left, queueNow);
        if (diff !== 0) return diff;
        return compareOperationalQueue(left, right);
      }
      if (queueSortMode === 'tp') {
        const leftTp = buildOperationalSnapshot(left, queueNow)?.tpRealMinutes ?? 0;
        const rightTp = buildOperationalSnapshot(right, queueNow)?.tpRealMinutes ?? 0;
        if (leftTp !== rightTp) return rightTp - leftTp;
        return compareOperationalQueue(left, right);
      }
      // 'risk': prioridade pelo risco de SLA (CRITICO → ALERTA → NORMAL)
      return compareQueueByOperationalPriority(left, right, queueNow);
    };
    return [...claimedTasks, ...queueTasks].sort(sortFn);
  }, [claimedTasks, queueTasks, queueNow, queueSortMode]);

  // ─── Inbox Rail — Unified queue (workflow + actions + problems) ────────────
  // Sort spec: criticidade máxima → proximidade do estouro de SLA → FIFO.
  const inboxRailTasks = useMemo<TaskListItem[]>(() => {
    const unified = unifiedWorkQuery.data ?? [];
    const seenIds = new Set(prioritizedDeskTasks.map((t) => t.id));

    const synthetic: TaskListItem[] = unified
      .filter((i) => {
        if (i.status === 'completed') return false;
        if (i.source === 'action') {
          return actionVisibleOnDesk({
            assignedTo: i.assigned_to,
            viewerId: userId,
            status: i.source_context?.action_status ?? i.status,
          });
        }
        return i.source === 'problem';
      })
      .filter((i) => !seenIds.has(i.source_id))
      .map((i): TaskListItem => {
        const severity = (i.source_context?.issue_severity ?? '').toLowerCase();
        const rawPriority = (i.priority ?? '').toLowerCase();
        const priority: TaskListItem['priority'] =
          severity === 'critical' || rawPriority === 'critical' || rawPriority === 'critica' ? 'critica'
          : severity === 'high' || rawPriority === 'high' || rawPriority === 'alta' ? 'alta'
          : severity === 'medium' || rawPriority === 'medium' || rawPriority === 'media' ? 'media'
          : severity === 'low' || rawPriority === 'low' || rawPriority === 'baixa' ? 'baixa'
          : undefined;
        const codePrefix = i.source === 'action' ? 'AC' : 'PRB';
        const referenceCode = `${codePrefix}-${i.source_id.slice(0, 6).toUpperCase()}`;
        const status: TaskListItem['status'] =
          i.status === 'in_progress' ? 'in_progress'
          : i.status === 'waiting' ? 'waiting'
          : 'pending';
        return {
          id: i.source_id,
          instanceId: i.source_id,
          instanceTitle: i.title,
          referenceCode,
          stageId: i.source_id,
          stageName: i.title,
          status,
          isOverdue: i.is_overdue,
          createdAt: i.created_at,
          updatedAt: i.created_at,
          organizationId: i.organization_id,
          slaDeadline: i.due_date ?? null,
          priority,
          metadata: {
            source: i.source,
            problem_id: i.source_context?.problem_id ?? null,
            managerial_priority: i.source_context?.managerial_priority ?? i.priority ?? null,
            operational_signal: i.source_context?.operational_signal ?? null,
          },
        };
      });

    const combined = [...prioritizedDeskTasks, ...synthetic];

    // Prompt 37 · Sort dirigido pelo Ribbon (FIFO / SLA / Lead Time / Idle / Priority).
    // Antes esta lista usava sort fixo (risk→sla→fifo) e ignorava o dropdown.
    const sortMode = workFilters.sort;
    const nowMs = Date.now();
    const SLA_NONE = Number.MAX_SAFE_INTEGER;
    const getRem = (t: TaskListItem): number => {
      const remaining = getRemainingMinutesFromDeadline(t.slaDeadline);
      return remaining == null || !Number.isFinite(remaining) ? SLA_NONE : remaining;
    };
    const getCreated = (t: TaskListItem): number => new Date(t.createdAt).getTime();
    const getLeadMs = (t: TaskListItem): number => nowMs - getCreated(t);
    const getIdleMs = (t: TaskListItem): number => {
      const ref = (t as { startedAt?: string }).startedAt ?? t.updatedAt ?? t.createdAt;
      return nowMs - new Date(ref).getTime();
    };
    const priorityRank: Record<string, number> = { critica: 0, alta: 1, media: 2, baixa: 3 };

    combined.sort((a, b) => {
      switch (sortMode) {
        case 'fifo':
          return getCreated(a) - getCreated(b);
        case 'sla_asc': {
          const dr = getRem(a) - getRem(b);
          if (dr !== 0) return dr;
          return getCreated(a) - getCreated(b);
        }
        case 'lead_desc':
          return getLeadMs(b) - getLeadMs(a);
        case 'idle_desc':
          return getIdleMs(b) - getIdleMs(a);
        case 'priority': {
          const pA = priorityRank[a.priority ?? ''] ?? 9;
          const pB = priorityRank[b.priority ?? ''] ?? 9;
          if (pA !== pB) return pA - pB;
          return getRem(a) - getRem(b);
        }
        case 'fila':
        default: {
          const rankOf = (task: TaskListItem) => {
            if (task.isOverdue) return 0;
            const risk = getOperationalRiskLevel(
              getRemainingMinutesFromDeadline(task.slaDeadline),
              getTaskTotalSlaMinutes(task),
            );
            if (risk === 'CRITICO') return 0;
            if (risk === 'ALERTA') return 1;
            if (task.status === 'in_progress' || task.status === 'paused') return 2;
            return 3;
          };
          const dr = rankOf(a) - rankOf(b);
          if (dr !== 0) return dr;
          const dRem = getRem(a) - getRem(b);
          if (dRem !== 0) return dRem;
          const dIdle = getIdleMs(b) - getIdleMs(a);
          if (dIdle !== 0) return dIdle;
          return getCreated(a) - getCreated(b);
        }
      }
    });

    const sourceOf = (task: TaskListItem): 'action' | 'workflow' | 'problem' | 'monitoring' => {
      const meta = (task.metadata ?? {}) as Record<string, unknown>;
      const src = meta.source ?? meta.workItemSource;
      if (src === 'action' || src === 'problem' || src === 'monitoring') return src;
      return 'workflow';
    };

    const sourceFilter = workFilters.sourceTypes;
    const query = executionFilter.trim().toLowerCase();
    return combined.filter((task) => {
      if (sourceFilter.length > 0 && !sourceFilter.includes(sourceOf(task))) return false;
      if (!query) return true;
      const haystack = [task.stageName, task.instanceTitle, task.referenceCode]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [prioritizedDeskTasks, unifiedWorkQuery.data, workFilters.sort, workFilters.sourceTypes, executionFilter, userId]);

  const mesaGridColumns = useMemo(() => {
    const avgDurationMin = calcMediaDuracao(prioritizedDeskTasks);
    const positionMap = new Map<string, number>(
      prioritizedDeskTasks.map((t, i) => [t.id, i]),
    );
    const etaContext: EtaContext = { avgDurationMin, positionMap, nowMs: liveNow };
    return taskListColumns(
      (task) => void mutations.claimTask(task.id),
      queueSortMode,
      etaContext,
    );
  }, [mutations, queueSortMode, prioritizedDeskTasks, liveNow]);

  useEffect(() => {
    const timer = window.setInterval(() => setLiveNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Show bottom action button only when header button is scrolled out of view
  const headerActionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = headerActionRef.current;
    if (!el) { setShowBottomAction(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setShowBottomAction(!entry.isIntersecting),
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedTask]);

  useEffect(() => {
    const timer = window.setInterval(() => setQueueNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setOperationalMetrics({
      userWip: claimedTasks.length,
      globalSla: kpis?.my_sla_rate_7d ?? 0,
      bottleneckStage: selectedTask?.stageName ?? null,
    });
  }, [claimedTasks.length, kpis?.my_sla_rate_7d, selectedTask?.stageName, setOperationalMetrics]);

  const prevSyncedTaskRef = useRef<string | null>(null);

  // Reset sync ref when task is deselected
  useEffect(() => {
    if (!selectedTaskId) {
      prevSyncedTaskRef.current = null;
    }
  }, [selectedTaskId]);

  useEffect(() => {
    if (!selectedTask) return;

    setActiveExecution(selectedTask.instanceId, selectedTask.referenceCode ?? selectedTask.instanceTitle ?? selectedTask.instanceId);
    setActiveStage(selectedTask.stageId, selectedTask.stageName);

    // Only sync form/checklist state when switching to a different task
    if (prevSyncedTaskRef.current === selectedTask.id) return;
    prevSyncedTaskRef.current = selectedTask.id;
    setExecutionRail(null);
    setSubmitAttempted(false);

    setFormState(
      Object.keys(savedFormData).length > 0
        ? savedFormData
        : selectedTask.formData && Object.keys(selectedTask.formData).length > 0
          ? selectedTask.formData
          : {},
    );
    setChecklistState(
      Object.keys(savedChecklistData).length > 0
        ? savedChecklistData
        : selectedTask.checklistData && Object.keys(selectedTask.checklistData).length > 0
          ? selectedTask.checklistData
          : {},
    );
    setNotes(selectedTask.notes ?? '');
  }, [selectedTask, savedFormData, savedChecklistData, setActiveExecution, setActiveStage, setChecklistState, setFormState, setNotes]);

  // ── Autosave: field changes → v2 tables ──
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const handleFieldChangeWithAutosave = useCallback((fieldId: string, value: unknown) => {
    setFormField(fieldId, value);
    // Only persist to DB when fieldId is a real UUID (workflow_fields_v2).
    // Block-based IDs (e.g. "blk-...") are synthetic and not in the DB.
    if (selectedTask && organizationId && userId && UUID_RE.test(fieldId)) {
      void saveFieldResponse(selectedTask.id, organizationId, fieldId, value, userId);
    }
  }, [selectedTask, organizationId, userId, setFormField]);

  const handleChecklistToggleWithAutosave = useCallback((itemId: string) => {
    toggleChecklistItem(itemId);
    const newValue = !checklistState[itemId];
    if (selectedTask && organizationId && userId && UUID_RE.test(itemId)) {
      void saveChecklistResponse(selectedTask.id, organizationId, itemId, newValue, userId);
    }
  }, [selectedTask, organizationId, userId, checklistState, toggleChecklistItem]);

  // Inline detail for non-workflow items (action / problem) — Prompt 33
  const [selectedUnifiedItem, setSelectedUnifiedItem] = useState<UnifiedWorkItem | null>(null);

  useEffect(() => {
    if (selectedTaskId) setSelectedUnifiedItem(null);
  }, [selectedTaskId]);
  const [previewWorkItem, setPreviewWorkItem] = useState<UnifiedWorkItem | null>(null);

  const handleOverviewItemClick = useCallback((sourceId: string) => {
    const workItem = filteredWorkItems.find((i) => i.source_id === sourceId)
      ?? unifiedWorkItems.find((i) => i.source_id === sourceId);
    if (workItem) setPreviewWorkItem(workItem);
  }, [filteredWorkItems, unifiedWorkItems]);

  const handleSelectTask = useCallback((taskId: string) => {
    setPreviewWorkItem(null);
    const selected = tasks.find((item) => item.id === taskId);
    if (selected) {
      // Workflow task — open cockpit
      setSelectedUnifiedItem(null);
      setActiveExecution(selected.instanceId, selected.referenceCode ?? selected.instanceTitle ?? selected.instanceId);
      setActiveStage(selected.stageId, selected.stageName);
      globalSelectInstance(selected.instanceId);
      globalSelectTask(taskId);
      selectTask(taskId);
    } else {
      // Action / Problem — render INLINE no painel direito (sem mudar de rota)
      const workItem = unifiedWorkItems.find((i) => i.source_id === taskId);
      if (workItem && (workItem.source === 'action' || workItem.source === 'problem' || workItem.source === 'monitoring')) {
        selectTask(null);
        setActiveExecution(null, null);
        setActiveStage(null, null);
        setSelectedUnifiedItem(workItem);
      } else {
        selectTask(null);
        setSelectedUnifiedItem(null);
      }
    }
    setFormState({});
    setChecklistState({});
    setNotes('');
  }, [tasks, unifiedWorkItems, setActiveExecution, setActiveStage, globalSelectInstance, globalSelectTask, selectTask, setFormState, setChecklistState, setNotes]);

  const handleOpenExecutionFromPreview = useCallback(() => {
    if (!previewWorkItem) return;
    const taskId = previewWorkItem.source_id;
    setPreviewWorkItem(null);
    setWorkViewModeRaw('list');
    try { localStorage.setItem('workflow.minha-mesa.viewMode', 'list'); } catch { /* noop */ }
    handleSelectTask(taskId);
  }, [previewWorkItem, handleSelectTask]);

  // List stays open until user clicks a task — Central de Execução como landing

  useEffect(() => {
    if (selectedTaskId || activeTask?.id) return;

    setActiveExecution(null, null);
    setActiveStage(null, null);
  }, [activeTask?.id, selectedTaskId, setActiveExecution, setActiveStage]);

  const wipStatus = useMemo<WipStatus | null>(() => {
    if (!selectedTask || selectedTask.status !== 'pending') return null;
    const allLoaded = [...claimedTasks, ...queueTasks];
    return checkWIP(selectedTask.stageId, selectedTask.stageName, allLoaded);
  }, [selectedTask, claimedTasks, queueTasks]);

  const handleStartTask = useCallback(async () => {
    if (!selectedTask) return;
    if (selectedTask.status === 'pending') {
      try {
        await mutations.startTask(selectedTask.id);
        setFocusMode(true);
        showToast('success', 'Tarefa iniciada');
      } catch (error) {
        showToast('error', error instanceof Error ? error.message : 'Falha ao iniciar tarefa');
      }
    }
  }, [mutations, selectedTask, showToast]);

  const handlePauseTask = useCallback(async () => {
    if (!selectedTask) return;
    setIsPauseModalOpen(true);
  }, [selectedTask]);

  const confirmPauseTask = useCallback(async () => {
    if (!selectedTask || !pauseReason) return;
    try {
      await mutations.pauseTask({ taskId: selectedTask.id, reason: pauseReason });
      setIsPauseModalOpen(false);
      setFocusMode(false);
      showToast('info', 'Tarefa pausada');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao pausar tarefa');
    }
  }, [mutations, selectedTask, pauseReason, showToast]);

  const handleResumeTask = useCallback(async () => {
    if (!selectedTask) return;
    try {
      await mutations.resumeTask(selectedTask.id);
      setFocusMode(true);
      showToast('success', 'Tarefa retomada');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao retomar tarefa');
    }
  }, [mutations, selectedTask, showToast]);

  const handleClaimTask = useCallback(async () => {
    if (!selectedTask || !userId) return;
    try {
      await mutations.claimTask(selectedTask.id);
      await mutations.startTask(selectedTask.id);
      setFocusMode(true);
      showToast('success', '✔ Tarefa assumida com sucesso');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao assumir tarefa');
    }
  }, [mutations, selectedTask, userId, showToast]);

  const handleClaimOnly = useCallback(async () => {
    if (!selectedTask || !userId) return;
    try {
      await mutations.claimTask(selectedTask.id);
      showToast('success', '✔ Tarefa assumida');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao assumir tarefa');
    }
  }, [mutations, selectedTask, userId, showToast]);

  const handleStartOnly = useCallback(async () => {
    if (!selectedTask) return;
    try {
      await mutations.startTask(selectedTask.id);
      setFocusMode(true);
      showToast('success', '✔ Tarefa iniciada');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao iniciar tarefa');
    }
  }, [mutations, selectedTask, showToast]);

  const handleClaimQueueTask = useCallback(async (taskId: string) => {
    await mutations.claimTask(taskId);
  }, [mutations]);

  const focusFirstInvalidExecutionField = useCallback(() => {
    if (typeof document === 'undefined') return;
    setSubmitAttempted(true);

    // Delay to ensure React has rendered the tab content
    setTimeout(() => {
      const invalidWrapper = document.querySelector<HTMLElement>('[data-field-id][data-invalid="true"]');
      const invalidInput = invalidWrapper?.querySelector<HTMLElement>('input, textarea, select');

      if (invalidInput) {
        invalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => invalidInput.focus(), 300);
        if (invalidWrapper) {
          invalidWrapper.style.transition = 'box-shadow 0.3s ease';
          invalidWrapper.style.boxShadow = '0 0 0 4px rgba(239,68,68,0.45)';
          setTimeout(() => {
            invalidWrapper.style.transition = 'box-shadow 1s ease-out';
            invalidWrapper.style.boxShadow = '';
          }, 1500);
        }
        return;
      }

      // Fallback: try any required empty field
      const anyRequired = document.querySelector<HTMLElement>('[data-field-id] input:not(:disabled), [data-field-id] textarea:not(:disabled)');
      if (anyRequired) {
        anyRequired.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => anyRequired.focus(), 300);
        return;
      }

      validationSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const handleStartAndComplete = useCallback(async () => {
    if (!selectedTask || !userId) return;

    // Use v2 config fields/checklist if available, fallback to metadata
    const validationFields = execConfig?.fields?.length
      ? execConfig.fields.map(f => ({ ...f, options: f.options?.map(o => o.label) }))
      : selectedTask.formFields;
    const validationChecklist = execConfig?.checklist?.length
      ? execConfig.checklist.map(c => ({ id: c.id, label: c.label, required: c.required }))
      : selectedTask.checklist;

    // Execution Engine validation
    const validation = validateTaskCompletion({
      formFields: validationFields as any,
      formData: formState,
      checklist: validationChecklist,
      checklistData: checklistState,
      exitOptions: selectedTask.exitOptions,
      notes,
      attachmentCount: instanceArtifacts.length + savedAttachments.length,
      executionConfig: execConfig ? {
        fields: execConfig.fields,
        actionType: execConfig.completionType,
        rules: execConfig.rules,
        requireAllChecklist: execConfig.requireAllChecklist,
        requireChecklistOrder: execConfig.requireChecklistOrder,
        requireAttachment: execConfig.requireAttachment,
        requireJustification: execConfig.requireJustification,
        requirePhoto: execConfig.requirePhoto,
        slaHours: execConfig.slaHours ?? undefined,
      } : (selectedTask.metadata as any)?.executionConfig ?? null,
    });
    if (!validation.valid) {
      const firstError = validation.errors[0];
      focusFirstInvalidExecutionField();
      showToast('error', firstError.message);
      return;
    }

    // Save to v2 tables before completing
    if (organizationId) {
      await Promise.all([
        saveAllFieldResponses(selectedTask.id, organizationId, formState, userId),
        saveAllChecklistResponses(selectedTask.id, organizationId, checklistState, userId),
      ]);
    }

    if (selectedTask.status === 'pending') {
      await mutations.startTask(selectedTask.id);
    }

    await mutations.completeTask({
      taskId: selectedTask.id,
      instanceId: selectedTask.instanceId,
      result: 'completed',
      notes,
      formData: formState,
      checklistData: checklistState,
      completedBy: userId,
      previousUpdatedAt: selectedTask.updatedAt,
      blueprintVersionId: selectedTask.metadata?.blueprint_version_id as string | undefined,
    });

    // Log completion event (non-blocking)
    if (organizationId) {
      void logExecutionEvent(selectedTask.id, selectedTask.instanceId, organizationId, userId, 'task_completed', {
        result: 'completed',
        fromV2Tables,
        fieldCount: Object.keys(formState).length,
        checklistCount: Object.keys(checklistState).length,
      });

      // BI: calcular métricas + auto-problema + alertas (non-blocking)
      void processTaskCompletion({
        taskId: selectedTask.id,
        stageId: selectedTask.stageId,
        instanceId: selectedTask.instanceId,
        organizationId,
        workflowTemplateId: selectedTask.metadata?.workflow_template_id as string | undefined,
        completedBy: userId,
        assignedRoleId: selectedTask.metadata?.assigned_role_id as string | undefined,
        enteredAt: selectedTask.metadata?.entered_at as string | null ?? null,
        claimedAt: selectedTask.claimedAt ?? null,
        startedAt: selectedTask.startedAt ?? null,
        completedAt: new Date().toISOString(),
        formFields: (execConfig?.fields ?? selectedTask.formFields ?? []) as any,
        formData: formState,
        checklist: (execConfig?.checklist ?? selectedTask.checklist ?? []) as any,
        checklistData: checklistState,
        attachmentCount: instanceArtifacts.length + savedAttachments.length,
        rules: execConfig?.rules,
        slaHours: execConfig?.slaHours ?? (selectedTask.metadata?.sla_hours as number | undefined) ?? null,
        reworkCount: selectedTask.metadata?.rework_count as number | undefined,
      }, selectedTask.stageName ?? selectedTask.stageId, selectedTask.assignedUserId ?? userId);
    }
  }, [selectedTask, userId, notes, formState, checklistState, mutations, instanceArtifacts, savedAttachments, showToast, execConfig, organizationId, fromV2Tables, focusFirstInvalidExecutionField]);

  const handleApprove = useCallback(async () => {
    if (!selectedTask || !userId) return;

    const validationFields = execConfig?.fields?.length
      ? execConfig.fields.map(f => ({ ...f, options: f.options?.map(o => o.label) }))
      : selectedTask.formFields;
    const validationChecklist = execConfig?.checklist?.length
      ? execConfig.checklist.map(c => ({ id: c.id, label: c.label, required: c.required }))
      : selectedTask.checklist;

    const validation = validateTaskCompletion({
      formFields: validationFields as any,
      formData: formState,
      checklist: validationChecklist,
      checklistData: checklistState,
      exitOptions: selectedTask.exitOptions,
      notes,
      attachmentCount: instanceArtifacts.length + savedAttachments.length,
      executionConfig: execConfig ? {
        fields: execConfig.fields,
        actionType: execConfig.completionType,
        rules: execConfig.rules,
        requireAllChecklist: execConfig.requireAllChecklist,
        requireChecklistOrder: execConfig.requireChecklistOrder,
        requireAttachment: execConfig.requireAttachment,
        requireJustification: execConfig.requireJustification,
        requirePhoto: execConfig.requirePhoto,
        slaHours: execConfig.slaHours ?? undefined,
      } : (selectedTask.metadata as any)?.executionConfig ?? null,
    });
    if (!validation.valid) {
      const firstError = validation.errors[0];
      focusFirstInvalidExecutionField();
      showToast('error', firstError.message);
      return;
    }

    if (organizationId) {
      await Promise.all([
        saveAllFieldResponses(selectedTask.id, organizationId, formState, userId),
        saveAllChecklistResponses(selectedTask.id, organizationId, checklistState, userId),
      ]);
    }

    if (selectedTask.status === 'pending') {
      await mutations.startTask(selectedTask.id);
    }

    await mutations.completeTask({
      taskId: selectedTask.id,
      instanceId: selectedTask.instanceId,
      result: 'approved',
      notes,
      formData: formState,
      checklistData: checklistState,
      completedBy: userId,
      previousUpdatedAt: selectedTask.updatedAt,
      blueprintVersionId: selectedTask.metadata?.blueprint_version_id as string | undefined,
    });

    if (organizationId) {
      void logExecutionEvent(selectedTask.id, selectedTask.instanceId, organizationId, userId, 'task_completed', { result: 'approved', fromV2Tables });

      // BI: calcular métricas + auto-problema + alertas (non-blocking)
      void processTaskCompletion({
        taskId: selectedTask.id,
        stageId: selectedTask.stageId,
        instanceId: selectedTask.instanceId,
        organizationId,
        workflowTemplateId: selectedTask.metadata?.workflow_template_id as string | undefined,
        completedBy: userId,
        assignedRoleId: selectedTask.metadata?.assigned_role_id as string | undefined,
        enteredAt: selectedTask.metadata?.entered_at as string | null ?? null,
        claimedAt: selectedTask.claimedAt ?? null,
        startedAt: selectedTask.startedAt ?? null,
        completedAt: new Date().toISOString(),
        formFields: (execConfig?.fields ?? selectedTask.formFields ?? []) as any,
        formData: formState,
        checklist: (execConfig?.checklist ?? selectedTask.checklist ?? []) as any,
        checklistData: checklistState,
        attachmentCount: instanceArtifacts.length + savedAttachments.length,
        rules: execConfig?.rules,
        slaHours: execConfig?.slaHours ?? (selectedTask.metadata?.sla_hours as number | undefined) ?? null,
        reworkCount: selectedTask.metadata?.rework_count as number | undefined,
      }, selectedTask.stageName ?? selectedTask.stageId, selectedTask.assignedUserId ?? userId);
    }
  }, [selectedTask, userId, notes, formState, checklistState, mutations, instanceArtifacts, savedAttachments, showToast, execConfig, organizationId, fromV2Tables, focusFirstInvalidExecutionField]);

  const handleReject = useCallback(async (reason: string) => {
    if (!selectedTask) return;
    await mutations.rejectTask({ taskId: selectedTask.id, reason, instanceId: selectedTask.instanceId });
  }, [selectedTask, mutations]);

  const handleSaveForm = useCallback(async () => {
    if (!selectedTask) return;
    await mutations.saveForm({
      taskId: selectedTask.id,
      formData: formState,
      checklistData: checklistState,
    });
  }, [selectedTask, formState, checklistState, mutations]);

  const handlePullNextTask = useCallback(async () => {
    if (!nextRoleTask) {
      showToast('info', 'Nenhuma tarefa disponivel na fila');
      return;
    }

    try {
      await mutations.claimTask(nextRoleTask.id);
      handleSelectTask(nextRoleTask.id);
      showToast('success', 'Proxima tarefa critica assumida');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao puxar a proxima tarefa');
    }
  }, [mutations, nextRoleTask, showToast, handleSelectTask]);

  // Cancel any pending auto-pull timer
  const cancelAutoPull = useCallback(() => {
    if (autoPullTimerRef.current) {
      clearTimeout(autoPullTimerRef.current);
      autoPullTimerRef.current = null;
    }
    if (autoPullTickRef.current) {
      clearInterval(autoPullTickRef.current);
      autoPullTickRef.current = null;
    }
    setAutoPullCountdown(3);
    setAutoPullPhase((prev) => (prev === 'pulling' ? prev : 'idle'));
  }, []);

  // Auto-pull: claim next task automatically
  const executeAutoPull = useCallback(async () => {
    if (!nextRoleTask) {
      setAutoPullPhase('idle');
      return;
    }

    // Debounce guard
    const now = Date.now();
    if (now - autoPullDebounceRef.current < AUTO_PULL_DEBOUNCE) {
      setAutoPullPhase('idle');
      return;
    }

    setAutoPullPhase('pulling');
    autoPullDebounceRef.current = now;

    try {
      await mutations.claimTask(nextRoleTask.id);
    } catch {
      setAutoPullPhase('error');
      // Reset error after 3s so timer can retry
      setTimeout(() => setAutoPullPhase('idle'), 3000);
    }
  }, [mutations, nextRoleTask]);

  // Auto-pull idle timer: starts 3s countdown when empty + queue available
  useEffect(() => {
    // Auto-pull: triggers after completing a task when queue has items
    const shouldAutoPull =
      !selectedTask &&
      !detailLoading &&
      !selectedTaskId &&
      queueTasks.length > 0 &&
      workViewMode === 'list' &&
      autoPullPhase === 'idle' &&
      !mutations.isClaiming;

    if (!shouldAutoPull) {
      // If conditions no longer met, cancel any pending timer
      if (autoPullTimerRef.current) {
        clearTimeout(autoPullTimerRef.current);
        autoPullTimerRef.current = null;
      }
      return;
    }

    setAutoPullPhase('counting');
    setAutoPullCountdown(3);

    // Tick countdown every second
    autoPullTickRef.current = setInterval(() => {
      setAutoPullCountdown((prev) => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    autoPullTimerRef.current = setTimeout(() => {
      autoPullTimerRef.current = null;
      if (autoPullTickRef.current) {
        clearInterval(autoPullTickRef.current);
        autoPullTickRef.current = null;
      }
      void executeAutoPull();
    }, AUTO_PULL_DELAY);

    return () => {
      if (autoPullTimerRef.current) {
        clearTimeout(autoPullTimerRef.current);
        autoPullTimerRef.current = null;
      }
      if (autoPullTickRef.current) {
        clearInterval(autoPullTickRef.current);
        autoPullTickRef.current = null;
      }
    };
  }, [selectedTask, detailLoading, selectedTaskId, queueTasks.length, workViewMode, autoPullPhase, mutations.isClaiming, executeAutoPull]);

  // Cancel auto-pull on user interaction (mouse, keyboard)
  useEffect(() => {
    const container = autoPullContainerRef.current;
    if (!container) return;
    if (autoPullPhase !== 'counting') return;

    const handleInteraction = () => cancelAutoPull();

    container.addEventListener('mousemove', handleInteraction, { passive: true });
    container.addEventListener('mousedown', handleInteraction, { passive: true });
    container.addEventListener('keydown', handleInteraction, { passive: true });

    return () => {
      container.removeEventListener('mousemove', handleInteraction);
      container.removeEventListener('mousedown', handleInteraction);
      container.removeEventListener('keydown', handleInteraction);
    };
  }, [autoPullPhase, cancelAutoPull]);

  // Reset auto-pull phase when a task becomes active (claim succeeded)
  useEffect(() => {
    if (selectedTask || activeTask) {
      setAutoPullPhase('idle');
    }
  }, [selectedTask, activeTask]);

  const handleOpenExecution = useCallback((taskItem?: Pick<TaskListItem, 'instanceId'> | null) => {
    if (!taskItem?.instanceId) return;
    navigate(`/execucoes/instancias/${encodeURIComponent(taskItem.instanceId)}`);
  }, [navigate]);

  const handleBackToList = useCallback(() => {
    setFocusMode(false);
    if (viewportMode === 'drawer') {
      setIsRightPanelOpen(false);
    }
    setSupportTab('demandas');
    setSupportPanelOpen(true);
    selectTask(null as any);
    resetTaskState();
    setActiveExecution(null, null);
    setActiveStage(null, null);
  }, [selectTask, resetTaskState, setActiveExecution, setActiveStage, viewportMode]);

  const canComplete = useMemo(() => {
    if (!selectedTask) return false;
    // Required form fields
    const requiredFields = selectedTask.formFields.filter((field) => field.required);
    const allFieldsFilled = requiredFields.every((field) => {
      const value = formState[field.id];
      return value !== undefined && value !== null && value !== '';
    });
    // Required checklist items
    const requiredChecklist = selectedTask.checklist.filter((item) => item.required);
    const allChecklistDone = requiredChecklist.every((item) => checklistState[item.id]);
    return allFieldsFilled && allChecklistDone;
  }, [formState, checklistState, selectedTask]);

  const isTaskStarted = selectedTask?.status === 'in_progress';
  const isTaskPaused = selectedTask?.status === 'paused';
  const isTaskActive = isTaskStarted || isTaskPaused;
  const isDecisionMode = Boolean(
    selectedTask?.completionType === 'approve_reject'
    || (selectedTask?.metadata as any)?.executionConfig?.actionType === 'approve_reject'
  );

  // Derive state machine state (resolves invariants like in_progress without executor)
  const taskState: TaskState = selectedTask
    ? resolveEffectiveState(selectedTask.status, Boolean(selectedTask.assignedUserId || selectedTask.assignedTo))
    : 'pending';

  const handleExportDesk = useCallback(() => {
    const csvRows = [
      ['dataset', 'execucao', 'etapa', 'status', 'prioridade', 'sla_restante_min', 'risco', 'tp_real_min', 'tempo_parado_min'],
      ...queueTasks.map((task) => {
        const snapshot = buildOperationalSnapshot(task, queueNow);
        const remainingMinutes = getRemainingMinutesFromDeadline(task.slaDeadline);
        const totalSlaMinutes = getTaskTotalSlaMinutes(task);
        const risk = getOperationalRiskPresentation(getOperationalRiskLevel(remainingMinutes, totalSlaMinutes));

        return [
          'fila',
          formatExecutionCode(task),
          task.stageName,
          task.status,
          formatPriority(task.priority),
          remainingMinutes ?? '',
          risk.label,
          snapshot?.tpRealMinutes?.toFixed(1) ?? '',
          snapshot?.queueMinutes?.toFixed(1) ?? '',
        ];
      }),
      ...linkedActions.map((action) => {
        const risk = getActionRisk(action.target_date);
        return [
          'acao',
          formatActionOrigin(action),
          action.title ?? formatActionType(action.type),
          action.status ?? '',
          '',
          '',
          risk.label,
          '',
          '',
        ];
      }),
    ];

    const csvContent = csvRows
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `minha-mesa-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    showToast('success', 'Exportacao operacional gerada');
  }, [linkedActions, queueNow, queueTasks, showToast]);

  const handleLinkAction = useCallback(() => {
    navigate('/acoes');
    showToast('info', 'Abrindo Plano para vinculo ou acompanhamento de acao');
  }, [navigate, showToast]);

  const handleEscalateTask = useCallback(async () => {
    if (!selectedTask) return;
    try {
      await mutations.escalateTask({ taskId: selectedTask.id, justification: `Escalonamento: ${selectedTask.stageName}` });
      showToast('success', `\u2714 Escalonamento registrado para ${selectedTask.stageName}`);
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao escalar tarefa');
    }
  }, [selectedTask, mutations, showToast]);

  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [registerProblemModalOpen, setRegisterProblemModalOpen] = useState(false);
  const [returnStepModalOpen, setReturnStepModalOpen] = useState(false);

  const { members: orgMembers } = useOrgMembers(organizationId);
  const userNamesMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of orgMembers) map[m.id] = m.name;
    return map;
  }, [orgMembers]);

  const handleTransferTask = useCallback(() => {
    if (!selectedTask) return;
    setTransferModalOpen(true);
  }, [selectedTask]);

  const handleTransferConfirm = useCallback(async (newUserId: string, newUserName: string, justification: string) => {
    if (!selectedTask) return;
    try {
      await mutations.transferTask({ taskId: selectedTask.id, newAssigneeId: newUserId, newAssigneeName: newUserName, justification });
      setTransferModalOpen(false);
      showToast('success', `\u2714 Tarefa transferida para ${newUserName}`);
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao transferir tarefa');
    }
  }, [selectedTask, mutations, showToast]);

  const handleReturnTask = useCallback(async () => {
    if (!selectedTask) return;
    try {
      await mutations.returnTask({ taskId: selectedTask.id, justification: 'Tarefa devolvida para fila de papel' });
      showToast('success', '\u2714 Tarefa devolvida para a fila');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao devolver tarefa');
    }
  }, [selectedTask, mutations, showToast]);

  const handleSaveNote = useCallback(async (note: string, files?: File[]) => {
    if (!selectedTask) return;
    try {
      let attachments: Array<{ name: string; url: string; size?: string }> | undefined;
      if (files && files.length > 0 && organizationId) {
        attachments = await uploadTaskAttachments(files, selectedTask.id, organizationId);
      }
      await mutations.addNote({ taskId: selectedTask.id, note: note || 'Anexo adicionado', attachments });
      showToast('success', attachments ? 'Observa\u00e7\u00e3o e anexo(s) registrados' : 'Observa\u00e7\u00e3o registrada');
      return true;
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao registrar observa\u00e7\u00e3o');
      return false;
    }
  }, [selectedTask, mutations, showToast, organizationId]);

  const handleRegisterProblem = useCallback(() => {
    if (!selectedTask) return;
    setRegisterProblemModalOpen(true);
  }, [selectedTask]);

  const handleRegisterProblemConfirm = useCallback(async (description: string, problemType: string, impact: string) => {
    if (!selectedTask) return;
    try {
      await mutations.registerProblem({ taskId: selectedTask.id, description, problemType, impact });
      setRegisterProblemModalOpen(false);
      showToast('success', '\u2714 Problema registrado');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao registrar problema');
    }
  }, [selectedTask, mutations, showToast]);

  // Prompt 34 — callbacks estáveis para o Ribbon (React.memo)
  const onRibbonClaim = useCallback(() => { void handleClaimOnly(); }, [handleClaimOnly]);
  const onRibbonStart = useCallback(() => { void handleStartOnly(); }, [handleStartOnly]);
  const onRibbonComplete = useCallback(() => { void handleStartAndComplete(); }, [handleStartAndComplete]);
  const onRibbonPause = useCallback(() => { void handlePauseTask(); }, [handlePauseTask]);
  const onRibbonResume = useCallback(() => { void handleResumeTask(); }, [handleResumeTask]);
  const onRibbonReject = useCallback(() => { if (selectedTask) setReturnStepModalOpen(true); }, [selectedTask]);
  const onRibbonToggleHistory = useCallback(() => { toggleDrawer('history'); }, [toggleDrawer]);
  const onRibbonPullNext = useCallback(() => { void handlePullNextTask(); }, [handlePullNextTask]);

  const handleOpenRequest = useCallback(() => {
    setWorkViewModeRaw('list');
    setFocusMode(false);
    try { localStorage.setItem('workflow.minha-mesa.viewMode', 'list'); } catch { /* noop */ }
    setSupportPanelOpen(true);
    setSupportTab('demandas');
    setLaunchFlow(null);
    setRequestCatalogOpen((open) => !open);
  }, []);

  useEffect(() => {
    if (!requestCatalogOpen && !launchFlow) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (launchFlow) setLaunchFlow(null);
      else setRequestCatalogOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestCatalogOpen, launchFlow]);

  const handleSupportTabChange = useCallback((tab: SupportPanelTab) => {
    if (requestCatalogOpen) {
      setRequestCatalogOpen(false);
      setLaunchFlow(null);
      setSupportTab(tab);
      setSupportPanelOpen(true);
      return;
    }
    if (tab === 'demandas' && supportPanelOpen && supportTab === 'demandas') {
      setSupportPanelOpen(false);
      return;
    }
    setSupportTab(tab);
    setSupportPanelOpen(true);
  }, [requestCatalogOpen, supportPanelOpen, supportTab]);

  const deskRibbon = useMemo(
    () => (
      <MinhaMesaRibbon
        searchValue={executionFilter}
        workViewMode={workViewMode}
        filters={workFilters}
        hasActiveFilters={hasActiveWorkFilters}
        density={cardDensity}
        focusMode={focusMode}
        cockpitActive={Boolean(selectedUnifiedItem)}
        taskState={taskState}
        canComplete={canComplete}
        onFiltersChange={setWorkFilters}
        onResetFilters={resetWorkFilters}
        onDensityChange={cycleDensity}
        onFocusModeToggle={toggleFocusMode}
        onSearchChange={setExecutionFilter}
        onSetWorkViewMode={handleSetWorkViewMode}
        onClaimTask={onRibbonClaim}
        onStartTask={onRibbonStart}
        onCompleteTask={onRibbonComplete}
        onPauseTask={onRibbonPause}
        onResumeTask={onRibbonResume}
        onRejectTask={onRibbonReject}
        onTransferTask={handleTransferTask}
        onEscalateTask={handleEscalateTask}
        onRegisterProblem={handleRegisterProblem}
        onToggleHistory={onRibbonToggleHistory}
        historyActive={activeDrawer === 'history'}
        isCompleting={mutations.isCompleting}
        isPausing={mutations.isPausing}
        isResuming={mutations.isResuming}
        isClaiming={mutations.isClaiming}
        isStarting={mutations.isStarting}
        onPullNextTask={onRibbonPullNext}
        canPullNext={Boolean(nextRoleTask)}
        queueSortMode={queueSortMode}
        onQueueSortModeChange={handleQueueSortModeChange}
        supportTab={supportTab}
        supportPanelOpen={supportPanelOpen}
        onSupportTabChange={handleSupportTabChange}
        supportSopDisabled={!selectedTask}
        supportHistoricoDisabled={!selectedTask}
        onOpenRequest={handleOpenRequest}
        requestCatalogOpen={requestCatalogOpen}
      />
    ),
    [
      selectedTask,
      selectedUnifiedItem,
      supportTab,
      supportPanelOpen,
      handleSupportTabChange,
      handleOpenRequest,
      requestCatalogOpen,
      taskState,
      canComplete,
      executionFilter,
      workViewMode,
      handleSetWorkViewMode,
      workFilters,
      hasActiveWorkFilters,
      setWorkFilters,
      resetWorkFilters,
      cardDensity,
      cycleDensity,
      focusMode,
      toggleFocusMode,
      onRibbonClaim,
      onRibbonStart,
      onRibbonComplete,
      onRibbonPause,
      onRibbonResume,
      onRibbonReject,
      onRibbonToggleHistory,
      onRibbonPullNext,
      handleTransferTask,
      handleEscalateTask,
      handleRegisterProblem,
      activeDrawer,
      mutations.isCompleting,
      mutations.isPausing,
      mutations.isResuming,
      mutations.isClaiming,
      mutations.isStarting,
      nextRoleTask,
      queueSortMode,
      handleQueueSortModeChange,
    ],
  );

  const selectedTaskOperational = useMemo(
    () => buildOperationalSnapshot(selectedTask, liveNow, logs),
    [selectedTask, liveNow, logs],
  );
  const selectedTaskRisk = selectedTask
    ? getOperationalRiskPresentation(getTaskRiskLevel(selectedTask))
    : null;
  const selectedTaskSlaRemaining = selectedTask
    ? getRemainingMinutesFromDeadline(selectedTask.slaDeadline)
    : null;
  const selectedTaskSlaInfo = formatSlaSmartLabel(selectedTaskSlaRemaining);
  const selectedTaskSlaLabel = selectedTaskSlaInfo.label;
  const selectedTaskSlaProgress = useMemo(() => {
    const totalMinutes = selectedTask ? getTaskTotalSlaMinutes(selectedTask) : null;
    if (selectedTaskSlaRemaining === null || totalMinutes === null || totalMinutes <= 0) {
      return selectedTaskSlaInfo.status === 'violated' ? 100 : 0;
    }

    const consumedPercent = ((totalMinutes - selectedTaskSlaRemaining) / totalMinutes) * 100;
    return Math.max(0, Math.min(100, consumedPercent));
  }, [selectedTask, selectedTaskSlaRemaining, selectedTaskSlaInfo.status]);
  const selectedTaskDeviation = getDeviationPresentation(selectedTaskOperational?.deviationPercent ?? null);
  const hasExecutor = Boolean(selectedTask?.assignedUserId || selectedTask?.assignedTo);
  const isCurrentUserExecutor = hasExecutor && (selectedTask?.assignedUserId === userId || selectedTask?.assignedTo === userId);
  const selectedTaskExecutor = hasExecutor
    ? (isCurrentUserExecutor ? 'Você' : (selectedTask?.assignedUserName ?? selectedTask?.assignedToName ?? 'Responsável não identificado'))
    : 'Não atribuído';

  // Handoff: last note/comment from another user
  const handoffData = useMemo(() => {
    if (!logs || logs.length === 0 || !userId) return null;
    // Find last note/comment from someone other than current user
    const handoffLog = logs.find((log: any) =>
      (log.action === 'note' || log.action === 'return' || log.action === 'reassign' || log.action === 'escalate') &&
      log.performed_by !== userId &&
      (log.justification ?? '').replace(/\.+$/, '').trim().length > 3
    );
    if (!handoffLog) return null;
    const rawMsg = ((handoffLog as any).justification ?? '').replace(/\.+$/, '').trim();
    const attachments = ((handoffLog as any).metadata?.attachments ?? []) as Array<{ name: string; url: string; size?: string }>;
    const userName = (handoffLog as any).performed_by_name
      ?? userNamesMap[(handoffLog as any).performed_by]
      ?? (handoffLog as any).performed_by.slice(0, 8);
    return {
      userName,
      timestamp: (handoffLog as any).timestamp ?? (handoffLog as any).created_at ?? '',
      message: rawMsg,
      attachments: attachments.length > 0 ? attachments : undefined,
    };
  }, [logs, userId, userNamesMap]);

  // Artifact references for handoff (lightweight ref list)
  const artifactRefs = useMemo(() => {
    return instanceArtifacts.map((a) => ({
      id: a.id,
      name: a.name,
      version: a.currentVersion,
      lastEditedAtStageName: a.lastEditedAtStageName,
    }));
  }, [instanceArtifacts]);

  // Smart instructions engine
  const smartInstructions = useMemo(() => {
    if (!selectedTask) return [];
    return computeSmartInstructions({
      slaStatus: selectedTaskSlaInfo.status,
      slaLabel: selectedTaskSlaLabel,
      handoff: handoffData,
      artifacts: instanceArtifacts,
      hasInstruction: Boolean(selectedTask.instruction),
      isOverdue: selectedTask.isOverdue ?? false,
    });
  }, [selectedTask, selectedTaskSlaInfo, selectedTaskSlaLabel, handoffData, instanceArtifacts]);

  // Last observation from logs (any user note)
  const lastObservation = useMemo(() => {
    if (!logs || logs.length === 0) return null;
    const noteLog = logs.find((log: any) =>
      (log.action === 'note' || log.action === 'return' || log.action === 'reassign' || log.action === 'escalate') &&
      (log.justification ?? '').replace(/\.+$/, '').trim().length > 3
    );
    if (!noteLog) return null;
    const raw = ((noteLog as any).justification ?? '').replace(/\.+$/, '').trim();
    const userName = (noteLog as any).performed_by_name
      ?? userNamesMap[(noteLog as any).performed_by]
      ?? (noteLog as any).performed_by?.slice(0, 8) ?? '—';
    const ts = (noteLog as any).timestamp ?? (noteLog as any).created_at ?? '';
    const ago = ts ? (() => {
      const diff = Date.now() - new Date(ts).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `há ${mins}min`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `há ${hrs}h`;
      return `há ${Math.floor(hrs / 24)}d`;
    })() : '';
    return { userName, ago, message: raw };
  }, [logs, userNamesMap]);

  const inboxOverdueCount = useMemo(
    () => inboxRailTasks.filter((t) => t.isOverdue).length,
    [inboxRailTasks],
  );

  const activeInstruction = execConfig?.instruction ?? selectedTask?.instruction ?? null;

  const panelBadges = useUnifiedPanelBadges({
    panelOpen: supportPanelOpen,
    activeTab: supportTab,
    taskId: selectedTaskId,
    queueCount: inboxRailTasks.length,
    overdueCount: inboxOverdueCount,
    logs,
    taskRevision: selectedTask?.updatedAt ?? null,
    hasInstruction: Boolean(activeInstruction),
  });

  const cockpitStatusItems = useMemo<StatusCapsuleItem[]>(() => {
    if (!selectedTask) return [];
    const stateDisplay = getStateDisplay(taskState);
    return [
      {
        label: 'SLA',
        value: selectedTaskSlaInfo.status === 'violated' ? `−${selectedTaskSlaLabel}` : selectedTaskSlaLabel,
        color: selectedTaskSlaInfo.status === 'violated' ? 'red' : selectedTaskSlaInfo.status === 'warning' ? 'amber' : selectedTaskSlaInfo.status === 'ok' ? 'emerald' : 'slate',
      },
      {
        label: 'Risco',
        value: selectedTaskRisk?.label ?? 'OK',
        color: selectedTaskRisk?.label === 'CRITICO' ? 'red' : selectedTaskRisk?.label === 'ALERTA' ? 'amber' : 'slate',
      },
      {
        label: 'Status',
        value: stateDisplay.label,
        color: stateDisplay.color,
      },
      {
        label: 'Resp.',
        value: !hasExecutor ? 'Sem resp.' : selectedTaskExecutor,
        color: !hasExecutor ? 'amber' : isCurrentUserExecutor ? 'emerald' : 'slate',
      },
    ];
  }, [selectedTask, selectedTaskSlaInfo, selectedTaskSlaLabel, selectedTaskRisk, taskState, hasExecutor, isCurrentUserExecutor, selectedTaskExecutor]);

  const cockpitSecondaryMetrics = useMemo<CockpitMetric[]>(() => {
    if (!selectedTask) return [];
    const slaValue =
      selectedTaskSlaInfo.status === 'none'
        ? 'Sem SLA'
        : selectedTaskSlaInfo.status === 'violated'
          ? `−${selectedTaskSlaLabel}`
          : selectedTaskSlaLabel;
    const idleMinutes = selectedTaskOperational?.queueMinutes ?? null;
    const tpValue = formatDurationClockFromMinutes(selectedTaskOperational?.tpRealMinutes) ?? '—';

    return [
      {
        label: 'SLA',
        value: slaValue,
        tone: selectedTaskSlaInfo.status === 'violated' ? 'danger' : selectedTaskSlaInfo.status === 'warning' ? 'warning' : 'default',
      },
      {
        label: 'Tempo total',
        value: tpValue,
        tone: 'info',
      },
      {
        label: 'Tempo parado',
        value: formatIdleDurationLabel(idleMinutes),
        tone: idleMinutes != null && idleMinutes > 60 * 24 ? 'danger' : idleMinutes != null && idleMinutes > 60 * 8 ? 'warning' : 'default',
      },
      {
        label: 'Prioridade',
        value: formatPriorityLabel(selectedTask.priority),
        tone: selectedTask.priority === 'critica' ? 'danger' : selectedTask.priority === 'alta' ? 'warning' : 'default',
      },
    ];
  }, [
    selectedTask,
    selectedTaskSlaInfo,
    selectedTaskSlaLabel,
    selectedTaskOperational,
  ]);

  const cockpitStatus = useMemo(() => {
    if (!selectedTask) {
      return { label: '—', tone: 'default' as CockpitMetric['tone'] };
    }
    const stateDisplay = getStateDisplay(taskState);
    return {
      label: stateDisplay.label.toUpperCase(),
      tone: (stateDisplay.color === 'red'
        ? 'danger'
        : stateDisplay.color === 'amber'
          ? 'warning'
          : stateDisplay.color === 'emerald'
            ? 'ok'
            : 'default') as CockpitMetric['tone'],
    };
  }, [selectedTask, taskState]);

  const cockpitStatusVariant = useMemo((): CockpitStatusVariant => {
    if (!selectedTask) return 'default';
    if (selectedTaskSlaInfo.status === 'violated' || selectedTask.isOverdue) return 'overdue';
    if (taskState === 'paused') return 'paused';
    if (taskState === 'in_progress') return 'in_progress';
    if (taskState === 'completed') return 'completed';
    return 'default';
  }, [selectedTask, selectedTaskSlaInfo.status, taskState]);

  const executionProcessTitle = useMemo(
    () => selectedTask?.instanceTitle ?? 'Operação',
    [selectedTask],
  );

  const executionStagePath = useMemo(
    () => selectedTask?.stageName ?? 'Etapa',
    [selectedTask],
  );

  const cockpitSlaLabel = useMemo(() => {
    if (!selectedTask) return '—';
    if (selectedTaskSlaInfo.status === 'none') return 'Sem SLA';
    if (selectedTaskSlaInfo.status === 'violated') return `−${selectedTaskSlaLabel}`;
    return selectedTaskSlaLabel;
  }, [selectedTask, selectedTaskSlaInfo.status, selectedTaskSlaLabel]);

  const cockpitSlaTone = useMemo((): 'default' | 'ok' | 'warning' | 'danger' => {
    if (selectedTaskSlaInfo.status === 'violated') return 'danger';
    if (selectedTaskSlaInfo.status === 'warning') return 'warning';
    if (selectedTaskSlaInfo.status === 'ok') return 'ok';
    return 'default';
  }, [selectedTaskSlaInfo.status]);

  const cockpitTimeLabel = useMemo(() => {
    const tp = formatDurationClockFromMinutes(selectedTaskOperational?.tpRealMinutes);
    return tp ?? '—';
  }, [selectedTaskOperational?.tpRealMinutes]);

  const cockpitTimeTone = useMemo((): 'default' | 'info' | 'warning' | 'danger' => {
    const idle = selectedTaskOperational?.queueMinutes ?? null;
    if (idle != null && idle > 60 * 24) return 'danger';
    if (idle != null && idle > 60 * 8) return 'warning';
    return 'info';
  }, [selectedTaskOperational?.queueMinutes]);

  const contextResponsible = useMemo(() => {
    if (!selectedTask) return '—';
    if (!hasExecutor) return 'Sem responsável';
    return isCurrentUserExecutor ? 'Você' : (selectedTaskExecutor ?? '—');
  }, [selectedTask, hasExecutor, isCurrentUserExecutor, selectedTaskExecutor]);

  const observationsCount = useMemo(
    () => logs.filter((log) => log.action === 'note').length,
    [logs],
  );

  const attachmentsCount = useMemo(
    () =>
      instanceArtifacts.length +
      savedAttachments.length +
      logs.filter((log) => log.action === 'attachment').length,
    [instanceArtifacts.length, savedAttachments.length, logs],
  );

  const executionTimelineEvents = useMemo(
    () => (selectedTask ? buildExecutionTimeline(selectedTask.createdAt, logs) : []),
    [selectedTask, logs],
  );

  const recentMovementEvents = useMemo(
    () => (selectedTask ? buildRecentMovementEvents(selectedTask.createdAt, logs) : []),
    [selectedTask, logs],
  );

  const openProblems = useMemo(
    () =>
      logs
        .filter((log) => log.action === 'register_problem')
        .map((log) => ({
          id: log.id,
          label: 'Problema operacional',
          detail: log.justification ?? undefined,
        })),
    [logs],
  );

  const contextPanelFields = useMemo(
    () =>
      selectedTask
        ? [
            { label: 'Demanda', value: selectedTask.instanceTitle ?? '—' },
            { label: 'Etapa', value: selectedTask.stageName },
            { label: 'Solicitante', value: resolveTaskRequesterName(selectedTask) },
            { label: 'Abertura', value: formatTaskOpenedAt(selectedTask.createdAt) },
            {
              label: 'SLA',
              value: selectedTaskSlaInfo.status === 'violated' ? `−${selectedTaskSlaLabel}` : selectedTaskSlaLabel,
              tone: (selectedTaskSlaInfo.status === 'violated' ? 'danger' : selectedTaskSlaInfo.status === 'warning' ? 'warning' : 'default') as const,
            },
            {
              label: 'Prioridade',
              value: formatPriorityLabel(selectedTask.priority),
            },
            {
              label: 'Risco',
              value: selectedTaskRisk?.label ?? 'OK',
              tone: (selectedTaskRisk?.label === 'CRITICO' ? 'danger' : 'default') as const,
            },
          ]
        : [],
    [selectedTask, selectedTaskSlaInfo, selectedTaskSlaLabel, selectedTaskRisk],
  );

  const attachmentFields = useMemo(
    () => selectedTask?.formFields.filter((field) => field.type === 'file') ?? [],
    [selectedTask],
  );
  const attachmentRecords = useMemo(() => {
    const raw = selectedTask?.metadata?.attachments;
    return Array.isArray(raw) ? raw as Array<{ id?: string; name?: string; size?: string; createdAt?: string }> : [];
  }, [selectedTask]);
  const requiresAttachment = Boolean(selectedTask?.exitOptions.requireAttachment || attachmentFields.length > 0);
  const selectedTaskRole = selectedTask?.assignedRoleName ?? selectedTask?.metadata?.roleName ?? selectedTask?.metadata?.role ?? 'Papel nao informado';
  const isCompactViewport = viewportMode === 'compact';
  const isDrawerViewport = viewportMode === 'drawer';
  const showSidebarSummary = Boolean(selectedTask) && !focusMode && !isDrawerViewport;

  const visibleExecutionFields = useMemo(() => {
    if (!selectedTask) return [];
    const configFields = execConfig?.fields?.length ? execConfig.fields : selectedTask.formFields;
    return getVisibleFields(configFields as any, formState);
  }, [selectedTask, execConfig, formState]);

  const liveWarnings = useMemo(() => {
    if (!selectedTask) return [];
    const configRules = execConfig?.rules ?? (selectedTask as any).executionConfig?.rules ?? [];
    return getActiveWarnings(configRules, formState);
  }, [selectedTask, execConfig, formState]);

  const liveValidation = useMemo(() => {
    if (!selectedTask) return { valid: true, errors: [] };

    if (hasBlocks && executionBlocks.length > 0) {
      return validateBlocks(executionBlocks, { ...blockResponses, ...formState });
    }

    const validationFields = execConfig?.fields?.length
      ? execConfig.fields.map((field) => ({ ...field, options: field.options?.map((option) => option.label) }))
      : selectedTask.formFields;
    const validationChecklist = execConfig?.checklist?.length
      ? execConfig.checklist.map((item) => ({ id: item.id, label: item.label, required: item.required }))
      : selectedTask.checklist;

    return validateTaskCompletion({
      formFields: validationFields as any,
      formData: formState,
      checklist: validationChecklist,
      checklistData: checklistState,
      exitOptions: selectedTask.exitOptions,
      notes,
      attachmentCount: instanceArtifacts.length + savedAttachments.length,
      executionConfig: execConfig ? {
        fields: execConfig.fields,
        actionType: execConfig.completionType,
        rules: execConfig.rules,
        requireAllChecklist: execConfig.requireAllChecklist,
        requireChecklistOrder: execConfig.requireChecklistOrder,
        requireAttachment: execConfig.requireAttachment,
        requireJustification: execConfig.requireJustification,
        requirePhoto: execConfig.requirePhoto,
        slaHours: execConfig.slaHours ?? undefined,
      } : (selectedTask.metadata as any)?.executionConfig ?? null,
    });
  }, [selectedTask, hasBlocks, executionBlocks, blockResponses, formState, checklistState, execConfig, notes, instanceArtifacts.length, savedAttachments.length]);

  const invalidFieldIds = useMemo(
    () => submitAttempted ? liveValidation.errors.map((error) => error.fieldId).filter((fieldId): fieldId is string => Boolean(fieldId)) : [],
    [liveValidation.errors, submitAttempted],
  );

  const firstInvalidFieldLabel = useMemo(() => {
    if (liveValidation.errors.length === 0) return null;
    // Always prefer the real field label over technical error message
    const firstWithField = liveValidation.errors.find(e => e.fieldId);
    if (firstWithField) {
      const field = visibleExecutionFields.find(f => f.id === firstWithField.fieldId);
      if (field) return field.label;
      // Try checklist
      const checkItem = selectedTask?.checklist.find(c => c.id === firstWithField.fieldId);
      if (checkItem) return checkItem.label;
    }
    // For non-field errors, extract label from message pattern "Campo 'X'" or "Item: X"
    const msg = liveValidation.errors[0]?.message ?? '';
    const match = msg.match(/["']([^"']+)["']/) ?? msg.match(/:\s*(.+)$/);
    return match?.[1] ?? null;
  }, [liveValidation.errors, visibleExecutionFields, selectedTask?.checklist]);

  const primaryActionConfig = useMemo(() => {
    if (!selectedTask) return null;

    if (!hasExecutor || taskState === 'unassigned') {
      return {
        label: 'INICIAR',
        loadingLabel: 'ASSUMINDO...',
        onClick: handleClaimTask,
        disabled: mutations.isClaiming || mutations.isStarting,
        busy: mutations.isClaiming || mutations.isStarting,
        tone: 'primary' as const,
      };
    }

    if (taskState === 'pending') {
      return {
        label: 'INICIAR',
        loadingLabel: 'INICIANDO...',
        onClick: handleStartOnly,
        disabled: mutations.isStarting || (wipStatus?.exceeded ?? false),
        busy: mutations.isStarting,
        tone: 'primary' as const,
      };
    }

    if (taskState === 'paused') {
      return {
        label: 'RETOMAR',
        loadingLabel: 'RETOMANDO...',
        onClick: handleResumeTask,
        disabled: mutations.isResuming,
        busy: mutations.isResuming,
        tone: 'primary' as const,
      };
    }

    if (taskState === 'in_progress') {
      const approvalFlow = selectedTask.completionType === 'approve_reject';
      if (approvalFlow) {
        return {
          label: 'APROVAR',
          loadingLabel: 'APROVANDO...',
          onClick: handleApprove,
          disabled: mutations.isCompleting,
          busy: mutations.isCompleting,
          tone: 'success' as const,
        };
      }
      // Poka-yoke: only green + "FINALIZAR" when all required fields are complete
      return {
        label: canComplete ? 'FINALIZAR TAREFA' : 'CONCLUIR',
        loadingLabel: 'CONCLUINDO...',
        onClick: canComplete ? handleStartAndComplete : focusFirstInvalidExecutionField,
        disabled: mutations.isCompleting,
        busy: mutations.isCompleting,
        tone: (canComplete ? 'success' : 'primary') as 'success' | 'primary',
      };
    }

    return null;
  }, [selectedTask, hasExecutor, taskState, mutations.isClaiming, mutations.isStarting, mutations.isResuming, mutations.isCompleting, handleClaimTask, handleStartOnly, handleResumeTask, handleApprove, handleStartAndComplete, canComplete, liveValidation.errors.length, focusFirstInvalidExecutionField, wipStatus]);

  const executionPrimaryAction = useMemo(() => {
    if (!primaryActionConfig) return null;
    const labelMap: Record<string, string> = {
      INICIAR: 'Iniciar Execução',
      RETOMAR: 'Retomar Execução',
      APROVAR: 'Aprovar',
      'FINALIZAR TAREFA': 'Finalizar Tarefa',
      CONCLUIR: 'Concluir',
    };
    return {
      ...primaryActionConfig,
      label: labelMap[primaryActionConfig.label] ?? primaryActionConfig.label,
    };
  }, [primaryActionConfig]);

  const pendencyItems = useMemo(() => {
    if (!selectedTask) return [];
    return buildPendencyItems({
      checklist: selectedTask.checklist ?? [],
      checklistState,
      validationErrors: liveValidation.errors,
      visibleFields: visibleExecutionFields,
      formState,
      requireJustification: Boolean(selectedTask.exitOptions.requireJustification),
      hasJustification: Boolean(notes.trim()),
      requireAttachment: Boolean(selectedTask.exitOptions.requireAttachment || attachmentFields.length > 0),
      attachmentCount: instanceArtifacts.length + savedAttachments.length,
    });
  }, [
    selectedTask,
    checklistState,
    liveValidation.errors,
    visibleExecutionFields,
    formState,
    notes,
    attachmentFields.length,
    instanceArtifacts.length,
    savedAttachments.length,
  ]);

  const executionProgressPct = useMemo(() => {
    if (!selectedTask) return 0;
    if (selectedTask.status === 'completed') return 100;
    if (taskState === 'pending' || taskState === 'unassigned') return 0;

    const requiredChecklist = (selectedTask.checklist ?? []).filter((c) => c.required);
    const checklistDone = requiredChecklist.filter((c) => checklistState[c.id]).length;
    const requiredFields = visibleExecutionFields.filter((f) => f.required);
    const fieldDone = requiredFields.filter((f) => !isFieldValueEmpty(formState[f.id])).length;
    const total = requiredChecklist.length + requiredFields.length;
    if (total === 0) {
      const allChecklist = selectedTask.checklist?.length ?? 0;
      if (allChecklist > 0) {
        const done = Object.values(checklistState).filter(Boolean).length;
        return Math.min(90, Math.round((done / allChecklist) * 100));
      }
      return 75;
    }
    return Math.min(90, Math.round(((checklistDone + fieldDone) / total) * 100));
  }, [selectedTask, taskState, checklistState, visibleExecutionFields, formState]);

  const formQuickSummary = useMemo(
    () => extractFormQuickSummary(visibleExecutionFields, formState),
    [visibleExecutionFields, formState],
  );

  const operationalSummaryData = useMemo<OperationalSummaryData | null>(() => {
    if (!selectedTask) return null;
    const stateDisplay = getStateDisplay(taskState);
    const idleMinutes = selectedTaskOperational?.queueMinutes ?? null;
    const registeredProblems = logs.filter((log) => log.action === 'register_problem').length;
    const pauseLog = logs.find((log) => log.action === 'status_change' && log.metadata?.to === 'paused');
    const reason =
      handoffData?.message ??
      pauseLog?.justification ??
      (selectedTask.completionType === 'approve_reject' && taskState === 'pending' ? 'Aguardando aprovação' : null) ??
      (taskState === 'paused' ? 'Execução pausada' : null);
    const lastMovement = lastObservation
      ? `${lastObservation.userName}: ${lastObservation.message}`
      : recentMovementEvents[0]
        ? `${recentMovementEvents[0].title} · ${recentMovementEvents[0].dateLabel}`
        : formatRelativeTime(selectedTask.updatedAt ?? selectedTask.createdAt);

    return {
      situation: stateDisplay.label.toUpperCase(),
      situationTone: cockpitStatus.tone,
      reason,
      openPendencies: pendencyItems.length,
      registeredProblems,
      executionTime: formatDurationClockFromMinutes(selectedTaskOperational?.tpRealMinutes) ?? '—',
      idleTime: formatIdleDurationLabel(idleMinutes),
      nextAction: executionPrimaryAction?.label ?? (!hasExecutor ? 'Assumir demanda' : 'Aguardando ação'),
      responsible: !hasExecutor ? 'Sem responsável' : isCurrentUserExecutor ? 'Você' : (selectedTaskExecutor ?? '—'),
      lastMovement,
    };
  }, [
    selectedTask,
    taskState,
    selectedTaskOperational,
    pendencyItems.length,
    logs,
    handoffData,
    lastObservation,
    cockpitStatus.tone,
    executionPrimaryAction,
    hasExecutor,
    isCurrentUserExecutor,
    selectedTaskExecutor,
    recentMovementEvents,
  ]);

  const cockpitOperationalLine = useMemo<CockpitMetric[]>(() => {
    if (!operationalSummaryData) return [];
    return [
      {
        label: 'Pendências',
        value: String(operationalSummaryData.openPendencies),
        tone: pendenciesSemaphoreTone(operationalSummaryData.openPendencies),
      },
      {
        label: 'Problemas',
        value: String(operationalSummaryData.registeredProblems),
        tone: problemsSemaphoreTone(operationalSummaryData.registeredProblems),
      },
      { label: 'Execução', value: operationalSummaryData.executionTime, tone: 'info' },
      {
        label: 'Parado',
        value: operationalSummaryData.idleTime,
        tone: operationalSummaryData.idleTime.includes('dia') ? 'danger' : operationalSummaryData.idleTime !== '—' ? 'warning' : 'default',
      },
    ];
  }, [operationalSummaryData]);

  const executionPrimaryActionWithSubtitle = useMemo(() => {
    if (!executionPrimaryAction) return null;
    const pendingChecklistCount = (selectedTask?.checklist ?? []).filter(
      (item) => item.required && !checklistState[item.id],
    ).length;
    const pendingFieldCount = pendencyItems.filter((item) => item.kind === 'field').length;
    const subtitle = primaryActionSubtitle({
      canComplete,
      pendenciesCount: pendencyItems.length,
      pendingChecklistCount,
      pendingFieldCount,
      primaryLabel: executionPrimaryAction.label,
    });
    return { ...executionPrimaryAction, subtitle };
  }, [executionPrimaryAction, selectedTask?.checklist, checklistState, pendencyItems, canComplete]);

  const contextNextSteps = useMemo(() => {
    if (!selectedTask) return [];
    const steps: Array<{ id: string; label: string }> = [];
    if (!hasExecutor) steps.push({ id: 'claim', label: 'Assumir responsabilidade da demanda' });
    if (taskState === 'pending' && hasExecutor) steps.push({ id: 'start', label: 'Iniciar execução da etapa' });
    if (taskState === 'paused') steps.push({ id: 'resume', label: 'Retomar execução' });
    if (taskState === 'in_progress') steps.push({ id: 'complete', label: 'Concluir etapa atual' });
    if (liveValidation.errors.length > 0) {
      steps.push({ id: 'pendencies', label: `Resolver ${liveValidation.errors.length} pendência(s) de formulário` });
    }
    steps.push({ id: 'next', label: 'Encaminhar para próxima etapa do fluxo' });
    return steps.slice(0, 5);
  }, [selectedTask, hasExecutor, taskState, liveValidation.errors.length]);

  const handleRequestSop = useCallback(() => {
    showToast('info', 'Solicitação de SOP registrada para revisão do processo.');
  }, [showToast]);

  return (
    <ModuleLayout ribbon={deskRibbon} className={workViewMode === 'list' && selectedTask ? 'mobile-viewport-lock' : ''}>
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--bg-workspace)]">

        {/* ════ KANBAN — acompanhamento (sem execução) ════ */}
        {workViewMode === 'kanban' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <MinhaMesaInbox notifications={notifications} onAcknowledge={markNotifRead} onAcknowledgeAll={markAllNotifsRead} onSelectTask={handleOverviewItemClick} />
            <WorkKanbanView items={filteredWorkItems} density={cardDensity} onCardClick={handleOverviewItemClick} />
          </div>
        )}

        {/* ════ TIMELINE — análise (sem execução) ════ */}
        {workViewMode === 'timeline' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <MinhaMesaInbox notifications={notifications} onAcknowledge={markNotifRead} onAcknowledgeAll={markAllNotifsRead} onSelectTask={handleOverviewItemClick} />
            <WorkTimelineView items={filteredWorkItems} onItemClick={handleOverviewItemClick} filtersActive={hasActiveWorkFilters} />
          </div>
        )}

        {/* ════ LISTA — execução (fila + cockpit + formulário) ════ */}
        {workViewMode === 'list' && (
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <UnifiedSupportPanel
              open={supportPanelOpen}
              onOpenChange={setSupportPanelOpen}
              activeTab={supportTab}
              onTabChange={setSupportTab}
              badges={panelBadges}
              hidden={focusMode}
              sopDisabled={!selectedTask}
              historicoDisabled={!selectedTask}
              demandasCount={inboxRailTasks.length}
              heading={requestCatalogOpen ? 'Nova Solicitação' : undefined}
              fill={requestCatalogOpen}
              demandasContent={
                requestCatalogOpen ? (
                  <RequestCatalogPanel onSelect={setLaunchFlow} />
                ) : (
                <div className="flex h-full min-h-0 flex-col">
                  <RoadmapAgendaStrip items={roadmapAgendaQuery.data ?? []} />
                  <div className="min-h-0 flex-1">
                    <InboxQueueRail
                      embedded
                      tasks={inboxRailTasks}
                      selectedTaskId={selectedUnifiedItem?.source_id ?? selectedTaskId}
                      onSelect={handleSelectTask}
                      isLoading={isLoading || unifiedWorkQuery.isLoading}
                    />
                  </div>
                </div>
                )
              }
              sopContent={
                !selectedTask ? (
                  <p className="px-4 py-8 text-center text-[11px] text-slate-400 dark:text-[var(--text-muted)]">
                    Selecione uma demanda na Carteira de Trabalho.
                  </p>
                ) : (
                  <div key={selectedTask.id} className="py-2 transition-opacity duration-300">
                    {smartInstructions.length > 0 && (
                      <div className="px-3 pb-2">
                        <SmartInstructionsBanner instructions={smartInstructions} />
                      </div>
                    )}
                    <ExecutionGuideStepper
                      instruction={activeInstruction}
                      activeStep={1}
                      onRequestSop={handleRequestSop}
                      onReportProblem={handleRegisterProblem}
                    />
                  </div>
                )
              }
              historicoContent={
                !selectedTask ? (
                  <p className="px-4 py-8 text-center text-[11px] text-slate-400 dark:text-[var(--text-muted)]">
                    Selecione uma demanda na Carteira de Trabalho.
                  </p>
                ) : (
                  <div key={selectedTask.id} className="transition-opacity duration-300">
                    {(handoffData || lastObservation) && (
                      <div className="space-y-2 border-b border-slate-100 px-4 py-3 dark:border-[var(--border-default)]">
                        {handoffData && (
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-[10px] text-amber-500">⚑</span>
                            <div className="min-w-0">
                              <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-amber-500">Passagem</p>
                              <p className="text-[10px] leading-relaxed text-slate-600 dark:text-[var(--text-secondary)]">{handoffData.message}</p>
                            </div>
                          </div>
                        )}
                        {lastObservation && (
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-[10px] text-slate-400">✎</span>
                            <div className="min-w-0">
                              <p className="font-mono text-[9px] text-slate-400">{lastObservation.userName} · {lastObservation.ago}</p>
                              <p className="text-[10px] leading-relaxed text-slate-600 dark:text-[var(--text-secondary)]">{lastObservation.message}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="px-3 py-2">
                      {logsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 size={16} className="animate-spin text-slate-400" />
                        </div>
                      ) : (
                        <>
                          <ExecutionTimelineSummary events={executionTimelineEvents} />
                          {logs.length > 0 && (
                            <div className="mt-3 border-t border-slate-100 pt-3 dark:border-[var(--border-default)]">
                              <MinhaMesaTimeline logs={logs} isLoading={false} userNames={userNamesMap} currentUserId={userId} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              }
            />

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {launchFlow ? (
          <RequestOpenStage
            key={launchFlow.id}
            flow={launchFlow}
            onCancel={() => setLaunchFlow(null)}
            onOpened={() => {
              setLaunchFlow(null);
              setRequestCatalogOpen(false);
              setSupportTab('demandas');
              setSupportPanelOpen(true);
            }}
          />
        ) : selectedTask ? (
          <div ref={autoPullContainerRef} className={`flex min-h-0 flex-1 flex-col bg-[var(--bg-workspace)] ${focusMode ? 'mm-focus-mode' : ''}`}>
            <TaskExecutionCockpit
              executionId={formatExecutionCode(selectedTask)}
              processTitle={executionProcessTitle}
              stagePath={executionStagePath}
              statusLabel={cockpitStatus.label}
              statusVariant={cockpitStatusVariant}
              progressPct={executionProgressPct}
              checklistDone={Object.values(checklistState).filter(Boolean).length}
              checklistTotal={selectedTask.checklist.length}
              pendenciesCount={pendencyItems.length}
              slaLabel={cockpitSlaLabel}
              slaTone={cockpitSlaTone}
              timeLabel={cockpitTimeLabel}
              timeTone={cockpitTimeTone}
              responsible={contextResponsible}
            />

            <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
              <TaskExecutionWorkbench
                key={selectedTask.id}
                task={selectedTask}
                hasBlocks={hasBlocks}
                executionBlocks={executionBlocks}
                blockResponses={blockResponses}
                formState={formState}
                visibleExecutionFields={visibleExecutionFields}
                checklist={selectedTask.checklist}
                checklistState={checklistState}
                invalidFieldIds={invalidFieldIds}
                isTaskPaused={isTaskPaused}
                isTaskCompleted={selectedTask.status === 'completed'}
                notes={notes}
                onNotesChange={setNotes}
                onFieldChange={handleFieldChangeWithAutosave}
                onChecklistToggle={handleChecklistToggleWithAutosave}
                onComplete={handleStartAndComplete}
                onApprove={handleApprove}
                onReject={handleReject}
                onSaveNotes={handleSaveNote}
                isCompleting={mutations.isCompleting}
                detailLoading={detailLoading}
                canComplete={canComplete}
                attachInputRef={attachInputRef}
                observationsCount={observationsCount}
                attachmentsCount={attachmentsCount}
                timelineEvents={executionTimelineEvents}
                readHandoff={handoffData?.message ?? null}
                readObservation={lastObservation}
                progressPct={executionProgressPct}
                instruction={activeInstruction}
                hasStarted={taskState === 'in_progress' || taskState === 'paused'}
                activityOpen={executionRail === 'activity'}
                referenceOpen={executionRail === 'reference'}
                onToggleActivity={() => setExecutionRail((current) => (current === 'activity' ? null : 'activity'))}
                onToggleReference={() => setExecutionRail((current) => (current === 'reference' ? null : 'reference'))}
              />

              {executionRail === 'reference' && (
              <TaskExecutionContextPanel
                responsible={contextResponsible}
                slaLabel={cockpitSlaLabel}
                slaTone={cockpitSlaTone}
                openedAtLabel={formatTaskOpenedAt(selectedTask.createdAt)}
                pendencyItems={pendencyItems}
                isLoading={detailLoading}
                onOpenDetails={() => setDemandDetailsOpen(true)}
                slaAlert={
                  selectedTaskSlaInfo.status === 'violated'
                    ? `SLA violado — ${selectedTaskSlaLabel} de atraso`
                    : selectedTaskSlaInfo.status === 'warning'
                      ? `SLA em risco — ${selectedTaskSlaLabel}`
                      : null
                }
              />
              )}
            </div>

            <ExecutionsActionBar
              onAttach={() => attachInputRef.current?.click()}
              onSave={() => void handleSaveNote(notes.trim())}
              onReportProblem={handleRegisterProblem}
              onDelegate={handleTransferTask}
              primaryAction={executionPrimaryActionWithSubtitle}
              saveDisabled={!notes.trim() || detailLoading}
              problemDisabled={selectedTask.status === 'completed'}
              delegateDisabled={!hasExecutor}
              pendenciesCount={pendencyItems.length}
            />

            <DemandDetailsDrawer
              open={demandDetailsOpen}
              onClose={() => setDemandDetailsOpen(false)}
              executionCode={formatExecutionCode(selectedTask)}
              instanceTitle={selectedTask.instanceTitle ?? null}
              requesterName={resolveTaskRequesterName(selectedTask)}
              openedAtLabel={formatTaskOpenedAt(selectedTask.createdAt)}
              slaLabel={selectedTaskSlaLabel}
              slaStatus={selectedTaskSlaInfo.status}
              riskLabel={selectedTaskRisk?.label ?? 'OK'}
              riskOk={selectedTaskRisk?.label !== 'CRITICO'}
              priority={selectedTask.priority ?? null}
              originLabel={selectedTask.instanceTitle ?? 'Workflow'}
            >
              {visibleExecutionFields.length > 0 && (
                <div className="border-t border-slate-100 dark:border-[var(--border-default)]">
                  <div className="px-4 py-2">
                    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.08em] text-slate-500">Campos da Etapa</span>
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-[var(--border-default)]">
                    {visibleExecutionFields.map((field) => {
                      const raw = formState[field.id];
                      const isEmpty = raw === '' || raw === null || raw === undefined;
                      const display: string = isEmpty ? '—' : Array.isArray(raw) ? (raw as string[]).join(', ') : String(raw);
                      return (
                        <div key={field.id} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 px-4 py-2.5">
                          <p className="truncate font-mono text-[9px] font-bold uppercase text-slate-400">{field.label}</p>
                          <p className={`text-right text-[11px] font-semibold ${isEmpty ? 'italic text-slate-300' : 'text-slate-900 dark:text-[var(--text-primary)]'}`}>{display}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </DemandDetailsDrawer>
          </div>
        ) : null}

        {/* Inline detail for non-workflow items (Ação / Problema) — Prompt 33 */}
        {!launchFlow && !selectedTask && selectedUnifiedItem && (
          <InlineSourceDetailPanel
            item={selectedUnifiedItem}
            organizationId={organizationId}
            onBack={() => setSelectedUnifiedItem(null)}
            onCompleted={() => setSelectedUnifiedItem(null)}
          />
        )}

        {/* Central de Execução Operacional — landing do módulo Tarefas */}
        {!launchFlow && !selectedTask && !selectedUnifiedItem && workViewMode === 'list' && (
          <OperationalExecutionCenter
            userName={user?.name ?? ''}
            claimedTasks={claimedTasks}
            queueTasks={queueTasks}
            inboxRailTasks={inboxRailTasks}
            unifiedWorkItems={unifiedWorkItems}
            kpis={kpis}
            avgDurationMin={calcMediaDuracao([...claimedTasks, ...queueTasks])}
            onSelectTask={handleSelectTask}
            onPullNext={handlePullNextTask}
            checkWip={checkWIP}
          />
        )}

            </div>{/* /Bancada */}
          </div>
        )}
        {/* ════ /List mode ════ */}

        <WorkItemPreviewDrawer
          item={previewWorkItem}
          onClose={() => setPreviewWorkItem(null)}
          onOpenExecution={handleOpenExecutionFromPreview}
        />

      <ToastContainer />
      <RegisterProblemModal
        open={registerProblemModalOpen}
        onClose={() => setRegisterProblemModalOpen(false)}
        onConfirm={handleRegisterProblemConfirm}
        isLoading={mutations.isRegisteringProblem}
      />
      <ReturnStepModal
        open={returnStepModalOpen}
        onClose={() => setReturnStepModalOpen(false)}
        onConfirm={async (justification, files) => {
          if (!selectedTask) return;
          try {
            let attachments: Array<{ name: string; url: string; size?: string }> | undefined;
            if (files && files.length > 0 && organizationId) {
              attachments = await uploadTaskAttachments(files, selectedTask.id, organizationId);
            }
            await mutations.rejectTask({ taskId: selectedTask.id, reason: justification, instanceId: selectedTask.instanceId });
            if (attachments) {
              await mutations.addNote({ taskId: selectedTask.id, note: 'Evidência anexada na devolução', attachments });
            }
            setReturnStepModalOpen(false);
            showToast('success', '\u2714 Etapa devolvida');
          } catch (error) {
            showToast('error', error instanceof Error ? error.message : 'Falha ao devolver etapa');
          }
        }}
        isLoading={mutations.isRejecting}
      />
      <TaskTransferModal
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        onConfirm={handleTransferConfirm}
        currentUserId={userId ?? ''}
        organizationId={organizationId ?? ''}
        isLoading={mutations.isTransferring}
      />
      
      {/* Pause Reason Modal */}
      {isPauseModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-[10px] border border-slate-700 bg-slate-800 p-5 shadow-2xl">
            <h3 className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-slate-100">
              <AlertTriangle size={16} className="text-amber-500" />
              Motivo da Pausa (NVA)
            </h3>
            <p className="mb-4 text-[11px] text-slate-400 leading-tight">
              Aponte o que bloqueou seu progresso nesta tarefa.
            </p>
            
            <div className="flex flex-col gap-2 mb-5">
              {['Aguardando Cliente', 'Falta de Documentação', 'Aguardando Aprovação', 'Problema Técnico', 'Fila de Análise', 'Aguardando Fornecedor'].map(reason => (
                <label key={reason} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio" 
                    name="pauseReason" 
                    value={reason} 
                    className="h-3 w-3 accent-amber-500 bg-slate-700 border-slate-600 focus:ring-amber-500/50 cursor-pointer"
                    checked={pauseReason === reason} 
                    onChange={(e) => setPauseReason(e.target.value)} 
                  />
                  <span className="text-[12px] text-slate-300 group-hover:text-slate-100 transition-colors">
                    {reason}
                  </span>
                </label>
              ))}
              
              <div className="mt-2 text-[11px] text-slate-400">Outro motivo:</div>
              <input
                type="text"
                placeholder="Exemplo: Reunião urgência"
                className="w-full h-[32px] rounded-md border border-slate-700 bg-slate-900/50 px-3 text-[12px] text-slate-200 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                onChange={(e) => {
                  if (e.target.value) {
                    setPauseReason(e.target.value);
                  }
                }}
              />
            </div>
            
            <div className="flex items-center justify-end gap-2 border-t border-slate-700 pt-3">
              <button
                onClick={() => setIsPauseModalOpen(false)}
                className="rounded-md px-3 py-1.5 text-[12px] font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmPauseTask}
                disabled={!pauseReason}
                className="flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-[12px] font-semibold text-amber-950 hover:bg-amber-400 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PauseCircle size={14} /> 
                Confirmar Pausa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleLayout>
  );
};
